// src/tabs/PdvTab.js
import React, { useState, useEffect, useRef, useMemo } from "react";
import { pb } from "../services/pocketbase";
import { inventoryService } from "../services/inventoryService";
import { reservationsService } from "../services/reservationsService";
import { logAction } from "../services/logService";
import { formatMoney } from "../utils/formatters";
import { financeiroIntegracaoService } from "../services/financeiroIntegracaoService";
import { useOperator } from "../contexts/OperatorContext";
import InvoiceGenerationModal from "../components/modals/InvoiceGenerationModal";
import { correiosService } from "../services/correiosService";
import EngravingDisplay from "../components/production/EngravingDisplay";
import { getEngravingBadgeStyle } from "../utils/engravingUtils";
import {
  ShoppingBag,
  Search,
  ShoppingCart,
  Trash2,
  Plus,
  Minus,
  User,
  MapPin,
  DollarSign,
  CreditCard,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  AlertCircle,
  Layers,
  History,
  Archive,
  RefreshCw,
  TrendingUp,
  Calendar,
  Compass,
  Edit,
  Check,
  X,
  Truck,
  FileText
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area
} from "recharts";

const STONE_COLORS = [
  "Branca",
  "Vermelha",
  "Rosa",
  "Azul",
  "Azul Claro",
  "Verde",
  "Verde Escuro",
  "Amarela",
  "Roxa",
  "Laranja",
  "Peridoto",
  "Sky",
  "Preta",
  "Marrom",
  "ESPECIAL",
  "SEM PEDRA",
];

const STONE_TYPES = ["Zircônia", "Natural", "Sem Pedra"];

const PAYMENT_METHODS = [
  { id: "pix", name: "PIX (10% OFF)", color: "border-teal-500 bg-teal-50/50 text-teal-700" },
  { id: "credito", name: "Cartão de Crédito", color: "border-blue-500 bg-blue-50/50 text-blue-700" },
  { id: "debito", name: "Cartão de Débito", color: "border-indigo-500 bg-indigo-50/50 text-indigo-700" },
  { id: "dinheiro", name: "Dinheiro (10% OFF)", color: "border-amber-500 bg-amber-50/50 text-amber-700" },
  { id: "outro", name: "Outro", color: "border-slate-500 bg-slate-50/50 text-slate-700" },
];

const normalizeText = (str) => {
  if (!str) return "";
  return String(str)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
};

const isProductInactive = (prod) => {
  if (!prod) return true;
  if (prod.isInactive === true) return true;
  if (prod.status === "inactive") return true;
  if (prod.full_data?.status === "inactive") return true;
  if (prod.full_data?.active === false) return true;
  if (prod.full_data?.situacao === "inativo" || prod.full_data?.situacao === "inactive") return true;
  return false;
};

const getEstimatedShipping = (stateCode, method) => {
  const uf = (stateCode || "").toUpperCase().trim();
  if (method === "retirada") return 0;
  
  // SP (Store State)
  if (uf === "SP") {
    if (method === "pac") return 15.00;
    if (method === "sedex") return 20.00;
    if (method === "motoboy") return 22.00;
    return 15.00;
  }
  
  // Southeast region
  if (["RJ", "MG", "ES"].includes(uf)) {
    if (method === "pac") return 20.00;
    if (method === "sedex") return 28.00;
    if (method === "motoboy") return 40.00;
    return 20.00;
  }
  
  // South & Center-West
  if (["PR", "SC", "RS", "DF", "GO", "MS", "MT"].includes(uf)) {
    if (method === "pac") return 25.00;
    if (method === "sedex") return 38.00;
    return 25.00;
  }
  
  // Northeast
  if (["BA", "PE", "CE", "RN", "PB", "AL", "SE", "MA", "PI"].includes(uf)) {
    if (method === "pac") return 32.00;
    if (method === "sedex") return 55.00;
    return 32.00;
  }
  
  // North
  if (["AM", "PA", "RO", "RR", "AC", "TO", "AP"].includes(uf)) {
    if (method === "pac") return 38.00;
    if (method === "sedex") return 75.00;
    return 38.00;
  }
  
  // Fallback
  if (method === "pac") return 20.00;
  if (method === "sedex") return 35.00;
  return 15.00;
};

export default function PdvTab({
  user,
  catalog = {},
  inventory = [],
  reservations = [],
  findCatalogItem,
  showNotification,
  refreshCatalog,
}) {
  const { activeOperator } = useOperator();
  const [activeSubtab, setActiveSubtab] = useState("vender"); // vender | historico | sincronizados | dashboard

  // --- ESTADOS DO CAIXA ---
  const [cart, setCart] = useState([]);
  const [orderNumber, setOrderNumber] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerCep, setCustomerCep] = useState("");
  const [customerCpf, setCustomerCpf] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [addressStreet, setAddressStreet] = useState("");
  const [addressNumber, setAddressNumber] = useState("");
  const [addressDistrict, setAddressDistrict] = useState("");
  const [addressCity, setAddressCity] = useState("");
  const [addressState, setAddressState] = useState("");
  const [addressComplement, setAddressComplement] = useState("");
  const [orderNotes, setOrderNotes] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("pix");
  const [discountType, setDiscountType] = useState("percent"); // percent | value
  const [discountValue, setDiscountValue] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingCep, setLoadingCep] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // --- NOVOS ESTADOS DE FRETE E FLUXOS FISCAIS ---
  const [saleMode, setSaleMode] = useState("retirada"); // retirada | entrega
  const [shippingMethod, setShippingMethod] = useState("retirada"); // retirada | pac | sedex | motoboy | outro
  const [shippingPrice, setShippingPrice] = useState(0);
  const [selectedSaleForInvoice, setSelectedSaleForInvoice] = useState(null);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [labelLoadingSaleId, setLabelLoadingSaleId] = useState(null);

  // --- MODAL DE ESPECIFICAÇÕES (Ao adicionar / editar) ---
  const [configuringProduct, setConfiguringProduct] = useState(null);
  const [configSpecs, setConfigSpecs] = useState({
    size: "",
    stoneColor: "Branca",
    stoneType: "Zircônia",
    stoneBatch: "",
    finishing: "",
    engraving: "",
    exception: "",
  });
  const [configSource, setConfigSource] = useState("estoque"); // estoque | producao
  const [configQty, setConfigQty] = useState(1);
  const [editingCartIndex, setEditingCartIndex] = useState(null); // Se != null, estamos editando um item existente no carrinho
  const [configPrice, setConfigPrice] = useState(0);
  const [additionalValue, setAdditionalValue] = useState("");

  // --- ESTADOS DO CADASTRO RÁPIDO DE PRODUTOS ---
  const [isRegisteringProduct, setIsRegisteringProduct] = useState(false);
  const [newProductData, setNewProductData] = useState({
    sku: "",
    name: "",
    stoneColor: "Branca",
    jewelryType: "",
    material: "",
    category: "",
    price: "",
  });
  const [isSubmittingProduct, setIsSubmittingProduct] = useState(false);

  // --- ESTADOS DO HISTÓRICO E POCKETBASE ---
  const [sales, setSales] = useState([]);
  const [loadingSales, setLoadingSales] = useState(true);

  // --- ESTADOS DO DASHBOARD ---
  const [dashPeriod, setDashPeriod] = useState("30"); // 7 | 15 | 30 | custom
  const [dashStartDate, setDashStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split("T")[0];
  });
  const [dashEndDate, setDashEndDate] = useState(() => new Date().toISOString().split("T")[0]);

  // --- AUTO-GENERATOR DE NÚMERO DE PEDIDO ---
  const generateOrderNumber = () => {
    const timestamp = Date.now().toString().slice(-6);
    const rand = Math.floor(100 + Math.random() * 900);
    return `PDV-${timestamp}${rand}-LJ01`;
  };

  useEffect(() => {
    if (activeSubtab === "vender" && !orderNumber) {
      setOrderNumber(generateOrderNumber());
    }
  }, [activeSubtab]);

  // --- CARREGAMENTO REALTIME DE VENDAS DO POCKETBASE ---
  useEffect(() => {
    let active = true;
    const fetchSales = async () => {
      setLoadingSales(true);
      try {
        const records = await pb.collection("pdv_sales").getFullList({
          sort: "-created",
        });
        if (active) setSales(records);
      } catch (err) {
        console.error("Erro ao carregar vendas do PDV:", err);
      } finally {
        if (active) setLoadingSales(false);
      }
    };

    fetchSales();

    // Inscrição Realtime
    pb.collection("pdv_sales").subscribe("*", (e) => {
      setSales((prev) => {
        if (e.action === "delete") return prev.filter((s) => s.id !== e.record.id);
        if (e.action === "create") return [e.record, ...prev];
        if (e.action === "update") {
          return prev.map((s) => (s.id === e.record.id ? e.record : s));
        }
        return prev;
      });
    });

    return () => {
      active = false;
      pb.collection("pdv_sales").unsubscribe("*").catch(() => {});
    };
  }, []);

  // --- VIA CEP API LOOKUP ---
  useEffect(() => {
    const cleanCep = customerCep.replace(/\D/g, "");
    if (cleanCep.length === 8) {
      const lookupCep = async () => {
        setLoadingCep(true);
        try {
          const res = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
          const data = await res.json();
          if (data.erro) {
            showNotification && showNotification("CEP não encontrado.", "error");
          } else {
            setAddressStreet(data.logradouro || "");
            setAddressDistrict(data.bairro || "");
            setAddressCity(data.localidade || "");
            setAddressState(data.uf || "");
            showNotification && showNotification("Endereço autocompletado!", "success");
            
            // Se estiver em modo de entrega, calcula o frete automaticamente ao preencher a UF
            if (saleMode === "entrega" && data.uf) {
              const estimate = getEstimatedShipping(data.uf, shippingMethod === "retirada" ? "pac" : shippingMethod);
              setShippingPrice(estimate);
            }
          }
        } catch (err) {
          console.error("Erro ao buscar CEP:", err);
        } finally {
          setLoadingCep(false);
        }
      };
      lookupCep();
    }
  }, [customerCep]);

  // --- RECALCULO AUTOMATICO DE FRETE ---
  useEffect(() => {
    if (saleMode === "retirada") {
      setShippingMethod("retirada");
      setShippingPrice(0);
    } else {
      if (shippingMethod === "retirada") {
        setShippingMethod("pac");
      }
      const estimate = getEstimatedShipping(addressState || "SP", shippingMethod === "retirada" ? "pac" : shippingMethod);
      setShippingPrice(estimate);
    }
  }, [saleMode, shippingMethod, addressState]);

  // --- LISTA ÚNICA DE PRODUTOS DO CATÁLOGO ---
  const uniqueCatalogList = useMemo(() => {
    if (!catalog) return [];
    const seen = new Map();
    Object.values(catalog).forEach((item) => {
      if (item && item.sku) {
        const cleanSkuKey = String(item.sku).trim().toUpperCase();
        if (!seen.has(cleanSkuKey)) {
          seen.set(cleanSkuKey, item);
        }
      }
    });
    return Array.from(seen.values());
  }, [catalog]);

  // --- PESQUISA DE PRODUTOS EM MEMÓRIA (MELHORIA DE PESQUISA REAL E SEM INATIVOS) ---
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    
    const queryNormalized = normalizeText(searchQuery);
    const searchTerms = queryNormalized.split(/\s+/).filter(Boolean);
    if (searchTerms.length === 0) return [];

    // Filtra estritamente apenas produtos ATIVOS (ignora itens inativos)
    const activeProducts = uniqueCatalogList.filter((prod) => !isProductInactive(prod));

    const scored = [];
    const seenSkus = new Set();

    for (const product of activeProducts) {
      const skuNorm = normalizeText(product.sku);
      const nameNorm = normalizeText(product.name);
      const modelNorm = normalizeText(product.model || product.full_data?.model || product.full_data?.modelo || "");
      const categoryNorm = normalizeText(product.category || product.full_data?.category || "");
      const materialNorm = normalizeText(product.material || product.full_data?.material || "");
      const stoneNorm = normalizeText(product.stoneColor || product.full_data?.stoneColor || "");
      
      let tagsNorm = "";
      if (product.tags) {
        if (typeof product.tags === "string") {
          try {
            const parsed = JSON.parse(product.tags);
            if (Array.isArray(parsed)) {
              tagsNorm = normalizeText(parsed.map(t => `${t.group || ""} ${t.value || ""}`).join(" "));
            } else {
              tagsNorm = normalizeText(product.tags);
            }
          } catch (e) {
            tagsNorm = normalizeText(product.tags);
          }
        } else if (Array.isArray(product.tags)) {
          tagsNorm = normalizeText(product.tags.map(t => `${t.group || ""} ${t.value || ""}`).join(" "));
        }
      }

      const searchableText = `${skuNorm} ${nameNorm} ${modelNorm} ${categoryNorm} ${materialNorm} ${stoneNorm} ${tagsNorm}`;

      // Todos os termos digitados devem bater no texto pesquisável
      const allTermsMatch = searchTerms.every(term => searchableText.includes(term));

      if (allTermsMatch) {
        let score = 0;
        const fullTerm = searchTerms.join(" ");

        // Match exato de SKU
        if (skuNorm === fullTerm) score += 1000;
        else if (skuNorm.startsWith(fullTerm)) score += 500;
        else if (skuNorm.includes(fullTerm)) score += 200;

        // Match no nome
        if (nameNorm.startsWith(fullTerm)) score += 300;
        else if (nameNorm.includes(fullTerm)) score += 100;

        // Match no modelo
        if (modelNorm && modelNorm === fullTerm) score += 150;
        else if (modelNorm && modelNorm.includes(fullTerm)) score += 80;

        // Match na categoria / tags
        if (categoryNorm.includes(fullTerm)) score += 60;
        if (tagsNorm.includes(fullTerm)) score += 40;

        scored.push({ product, score });
        seenSkus.add(product.sku);
      }
    }

    // Tenta também resolver via findCatalogItem se for código/variação de aro
    if (findCatalogItem) {
      const resolved = findCatalogItem(searchQuery.trim());
      if (resolved && !isProductInactive(resolved) && !seenSkus.has(resolved.sku)) {
        scored.unshift({ product: resolved, score: 900 });
        seenSkus.add(resolved.sku);
      }
    }

    scored.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return (a.product.name || "").localeCompare(b.product.name || "");
    });

    return scored.slice(0, 40).map(s => s.product);
  }, [searchQuery, uniqueCatalogList, findCatalogItem]);

  // --- CONTAGEM DE ESTOQUE DISPONÍVEL ---
  const getSkuStockCount = (sku) => {
    return inventory.filter((i) => i.sku === sku && i.status === "in_stock").length;
  };

  // --- INICIAR CONFIGURAÇÃO PARA ADICIONAR AO CARRINHO ---
  const handleOpenConfigure = (product) => {
    const sku = product.sku.trim().toUpperCase();
    const stockQty = getSkuStockCount(sku);
    const standardColor = product.stoneColor || product.standardColor || product.full_data?.stoneColor || "Branca";

    setConfiguringProduct(product);
    setEditingCartIndex(null);
    setConfigSource(stockQty > 0 ? "estoque" : "producao");
    setConfigQty(1);
    setConfigPrice(Number(product.price || product.full_data?.price || 0));
    setConfigSpecs({
      size: "",
      stoneColor: STONE_COLORS.includes(standardColor) ? standardColor : "Branca",
      stoneType: "Zircônia",
      stoneBatch: "",
      finishing: "",
      engraving: "",
      exception: "",
    });
  };

  // --- ABRIR EDICAO DE ITEM NO CARRINHO ---
  const handleOpenEditCartItem = (index) => {
    const item = cart[index];
    const catalogProduct = findCatalogItem(item.sku) || { sku: item.sku, name: item.name };

    setConfiguringProduct(catalogProduct);
    setEditingCartIndex(index);
    setConfigSource(item.source);
    setConfigQty(item.quantity);
    setConfigPrice(item.price);
    setConfigSpecs({ ...item.specs });
  };

  // --- CONFIRMAR ADIÇÃO / EDIÇÃO NO CARRINHO ---
  const handleConfirmCartItem = () => {
    const sku = configuringProduct.sku.trim().toUpperCase();
    const stockQty = getSkuStockCount(sku);

    if (configSource === "estoque" && configQty > stockQty) {
      showNotification && showNotification(`Quantidade indisponível no estoque físico. Estoque atual: ${stockQty}`, "error");
      return;
    }

    if (editingCartIndex !== null) {
      // EDITANDO ITEM
      setCart(
        cart.map((item, idx) =>
          idx === editingCartIndex
            ? {
                ...item,
                price: Number(configPrice),
                quantity: configQty,
                source: configSource,
                specs: { ...configSpecs },
              }
            : item
        )
      );
      showNotification && showNotification("Item atualizado no carrinho!", "success");
    } else {
      // ADICIONANDO NOVO ITEM
      // Para o PDV Showroom, chaves de carrinho dependem dos specs para não agrupar tamanhos diferentes!
      const newItem = {
        sku: sku,
        name: configuringProduct.name || "Sem Nome",
        price: Number(configPrice),
        quantity: configQty,
        source: configSource,
        image: configuringProduct.image || configuringProduct.image_url || "",
        specs: { ...configSpecs },
      };

      // Adiciona direto (sempre separado para garantir distinção de specs!)
      setCart([...cart, newItem]);
      showNotification && showNotification("Item adicionado ao carrinho!", "success");
    }

    // Fecha modal
    setConfiguringProduct(null);
    setEditingCartIndex(null);
  };

  // --- REMOVER ITEM DO CARRINHO ---
  const removeFromCart = (index) => {
    setCart(cart.filter((_, i) => i !== index));
  };

  // --- SALVAR NOVO PRODUTO NO CATÁLOGO ---
  const handleRegisterProduct = async (e) => {
    if (e) e.preventDefault();

    let sku = newProductData.sku.trim().toUpperCase();
    const name = newProductData.name.trim();
    const price = Number(newProductData.price) || 0;
    const stoneColor = newProductData.stoneColor.trim();
    const jewelryType = newProductData.jewelryType.trim();
    const material = newProductData.material.trim();
    const category = newProductData.category.trim();

    if (!sku) {
      showNotification && showNotification("SKU é obrigatório.", "warning");
      return;
    }
    if (!name) {
      showNotification && showNotification("Nome é obrigatório.", "warning");
      return;
    }
    if (price <= 0) {
      showNotification && showNotification("Preço deve ser maior que zero.", "warning");
      return;
    }

    // Validação/Proteção de SKU: Forçar prefixo PDV-
    if (!sku.startsWith("PDV-")) {
      sku = `PDV-${sku}`;
    }

    setIsSubmittingProduct(true);
    try {
      // 1. Verificar se SKU já existe no catálogo
      let existingRecord = null;
      try {
        existingRecord = await pb.collection("catalog").getFirstListItem(`sku = "${sku}"`);
      } catch (err) {
        // Não existe, tudo bem
      }

      if (existingRecord) {
        showNotification && showNotification(`O SKU ${sku} já existe no catálogo.`, "error");
        setIsSubmittingProduct(false);
        return;
      }

      // 2. Criar tags estruturadas para compatibilidade
      const tags = [
        { group: "Cor da Pedra", value: stoneColor, hidden: 0, print: 1 },
        { group: "Tipo de Joia", value: jewelryType || "Não Definido", hidden: 0, print: 1 },
        { group: "Material", value: material || "Não Definido", hidden: 0, print: 1 },
        { group: "Categoria", value: category || "Não Definido", hidden: 0, print: 1 }
      ];

      const catalogFullData = {
        sku: sku,
        name: name,
        price: price,
        tags: JSON.stringify(tags),
        model: jewelryType,
        category: category,
        stoneColor: stoneColor,
        material: material,
        status: "active"
      };

      const catalogPayload = {
        sku: sku,
        name: name,
        image_url: "",
        full_data: catalogFullData
      };

      // 3. Salvar no PocketBase
      await pb.collection("catalog").create(catalogPayload);

      // 4. Log
      await logAction(
        user,
        "PDV",
        "CADASTRO_PRODUTO",
        `Produto ${sku} (${name}) cadastrado via PDV por ${user.name}. Preço: ${formatMoney(price)}`,
        { sku, name, price }
      );

      showNotification && showNotification(`Produto ${sku} cadastrado com sucesso!`, "success");

      // 5. Atualizar a cache do catálogo no frontend
      if (refreshCatalog) {
        await refreshCatalog();
      }

      // 6. Fechar modal e definir o termo de pesquisa como o novo SKU para facilitar a venda!
      setIsRegisteringProduct(false);
      setNewProductData({
        sku: "",
        name: "",
        stoneColor: "Branca",
        jewelryType: "",
        material: "",
        category: "",
        price: "",
      });
      setSearchQuery(sku);
    } catch (err) {
      console.error("Erro ao cadastrar produto:", err);
      showNotification && showNotification(`Erro ao cadastrar produto: ${err.message}`, "error");
    } finally {
      setIsSubmittingProduct(false);
    }
  };

  // --- CÁLCULOS TOTAIS ---
  const subtotal = useMemo(() => {
    return cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  }, [cart]);

  // Desconto Automático por Forma de Pagamento (10% OFF em PIX e Dinheiro)
  const paymentDiscountAmount = useMemo(() => {
    if (paymentMethod === "pix" || paymentMethod === "dinheiro") {
      return subtotal * 0.10;
    }
    return 0;
  }, [subtotal, paymentMethod]);

  // Desconto Manual Adicional
  const manualDiscountAmount = useMemo(() => {
    const val = Number(discountValue) || 0;
    if (val <= 0) return 0;
    if (discountType === "percent") {
      const rate = Math.min(val, 50) / 100; // Máximo 50%
      return subtotal * rate;
    } else {
      const maxDiscount = subtotal * 0.5;
      return Math.min(val, maxDiscount); // Máximo 50% do subtotal
    }
  }, [subtotal, discountType, discountValue]);

  // Desconto Total Consolidado (Pagamento + Manual acumulados)
  const totalDiscountAmount = useMemo(() => {
    return Math.min(subtotal, paymentDiscountAmount + manualDiscountAmount);
  }, [subtotal, paymentDiscountAmount, manualDiscountAmount]);

  const grandTotal = useMemo(() => {
    const addVal = Number(additionalValue) || 0;
    const freightVal = Number(shippingPrice) || 0;
    return Math.max(0, subtotal - totalDiscountAmount + freightVal + addVal);
  }, [subtotal, totalDiscountAmount, shippingPrice, additionalValue]);

  // --- SELEÇÃO DE FORMA DE PAGAMENTO ---
  const handleSelectPaymentMethod = (methodId) => {
    setPaymentMethod(methodId);
  };

  // --- LIMPAR CAMPOS DO FORM ---
  const clearCheckoutForm = () => {
    setCart([]);
    setCustomerName("");
    setCustomerCep("");
    setAddressStreet("");
    setAddressNumber("");
    setAddressDistrict("");
    setAddressCity("");
    setAddressState("");
    setAddressComplement("");
    setOrderNotes("");
    setPaymentMethod("pix");
    setDiscountType("percent");
    setDiscountValue("");
    setAdditionalValue("");
    setCustomerCpf("");
    setCustomerPhone("");
    setCustomerEmail("");
    setSaleMode("retirada");
    setShippingMethod("retirada");
    setShippingPrice(0);
    setOrderNumber(generateOrderNumber());
  };

  // --- FINALIZAR VENDA ---
  const handleCheckout = async () => {
    if (cart.length === 0) {
      showNotification && showNotification("O carrinho está vazio.", "warning");
      return;
    }
    if (!customerName.trim()) {
      showNotification && showNotification("Nome do cliente é obrigatório.", "warning");
      return;
    }
    if (!customerCpf.trim()) {
      showNotification && showNotification("CPF é obrigatório.", "warning");
      return;
    }
    if (!customerPhone.trim()) {
      showNotification && showNotification("Telefone é obrigatório.", "warning");
      return;
    }
    if (!customerEmail.trim()) {
      showNotification && showNotification("E-mail é obrigatório.", "warning");
      return;
    }

    // Se estiver em modo Entrega, valida os campos de endereço
    if (saleMode === "entrega") {
      if (!addressStreet.trim() || !addressNumber.trim() || !addressCity.trim()) {
        showNotification && showNotification("O endereço completo é obrigatório para entregas.", "warning");
        return;
      }
      if (customerCep.replace(/\D/g, "").length !== 8) {
        showNotification && showNotification("O CEP do cliente deve ter 8 dígitos.", "warning");
        return;
      }
    }

    const hasAddress = addressStreet.trim() || addressCity.trim();
    const formattedAddress = hasAddress
      ? `${addressStreet}, Nº ${addressNumber || "S/N"}${addressComplement ? `, ${addressComplement}` : ""}, Bairro ${addressDistrict}, ${addressCity} - ${addressState}. CEP: ${customerCep}`
      : "Retirada no Showroom (Sem Endereço Cadastrado)";

    const fullAddress = saleMode === "retirada"
      ? `[RETIRADA] ${formattedAddress}`
      : formattedAddress;

    setIsSubmitting(true);
    try {
      const itemsPayload = [];
      const reservationCreators = [];
      const finalNotes = additionalValue && Number(additionalValue) > 0
        ? `[Acréscimo: ${formatMoney(Number(additionalValue))}] [Frete: ${formatMoney(Number(shippingPrice))}] ${orderNotes}`.trim()
        : (shippingPrice > 0 ? `[Frete: ${formatMoney(Number(shippingPrice))}] ${orderNotes}`.trim() : orderNotes);

      for (let i = 0; i < cart.length; i++) {
        const cartItem = cart[i];
        const itemDetail = {
          sku: cartItem.sku,
          name: cartItem.name,
          price: cartItem.price,
          quantity: cartItem.quantity,
          source: cartItem.source,
          specs: cartItem.specs,
          soldItemIds: [],
          reservationIds: [],
        };

        // Prepara as funções para criação das reservas (executadas apenas após o pdv_sales ser criado)
        reservationCreators.push(async () => {
          const reservationPromises = [];
          for (let q = 0; q < cartItem.quantity; q++) {
            const payload = {
              sku: cartItem.sku,
              quantity: 1,
              status: "PENDENTE",
              notes: `[PDV Showroom - Pedido: ${orderNumber}] Fonte: ${
                cartItem.source === "estoque" ? "Estoque" : "Produção"
              }. Modalidade: ${saleMode === "retirada" ? "Retirada em Loja" : "Envio (" + shippingMethod.toUpperCase() + ")"}. Endereço: ${fullAddress}. Obs: ${finalNotes}`,
              created_by_name: user.name,
              source: "venda_presencial",
              customer_name: customerName,
              order_metadata: {
                customer: {
                  name: customerName,
                  address: fullAddress,
                  cpf: customerCpf.trim(),
                  phone: customerPhone.trim(),
                  email: customerEmail.trim(),
                },
                number: orderNumber,
                notes: finalNotes,
              },
              shipping: {
                tipoenvio: saleMode === "retirada" ? "Retirada" : shippingMethod.toUpperCase(),
                price: String(shippingPrice),
                tracking: "",
                address: {
                  destinatario: customerName,
                  street: addressStreet || "Retirada no Showroom",
                  number: addressNumber || "S/N",
                  complemento: addressComplement || "",
                  city: addressCity || "São Paulo",
                  statecode: addressState || "SP",
                  zip: customerCep || "00000-000",
                  district: addressDistrict || "Showroom",
                },
              },
              specs: {
                ...cartItem.specs,
                exception: cartItem.specs.exception || "",
              },
              exception: cartItem.specs.exception || "",
            };

            reservationPromises.push(
              pb.collection("reservations").create(payload)
            );
          }

          const createdReservations = await Promise.all(reservationPromises);
          return {
            index: i,
            ids: createdReservations.map((r) => r.id),
          };
        });

        itemsPayload.push(itemDetail);
      }

      // 1. Salva em pdv_sales primeiro.
      const createdSale = await pb.collection("pdv_sales").create({
        order_number: orderNumber.trim(),
        customer_name: customerName.trim(),
        customer_address: fullAddress.trim(),
        customer_phone: customerPhone.trim(),
        customer_cpf: customerCpf.trim(),
        customer_email: customerEmail.trim(),
        seller_name: activeOperator ? `PDV · ${activeOperator.nome}` : user.name,
        payment_method: paymentMethod,
        subtotal: subtotal,
        discount: totalDiscountAmount,
        total: grandTotal,
        items: itemsPayload,
        status: "PDV_PENDENTE",
        archived: false,
        synced: false,
      });

      // 2. Com a venda criada com sucesso, cria as reservas de produção/estoque
      const reservationResults = [];
      for (const creator of reservationCreators) {
        const result = await creator();
        reservationResults.push(result);
      }

      // 3. Atualiza os IDs das reservas geradas no itemsPayload
      reservationResults.forEach((res) => {
        itemsPayload[res.index].reservationIds = res.ids;
      });

      // 4. Salva as chaves de reservas de volta na venda pdv_sales
      await pb.collection("pdv_sales").update(createdSale.id, {
        items: itemsPayload,
      });

      // Log
      await logAction(
        user,
        "PDV",
        "VENDA",
        `Venda Showroom ${orderNumber} realizada para o cliente ${customerName}. Total: ${formatMoney(
          grandTotal
        )}`,
        { orderNumber, customerName, total: grandTotal }
      );

      // 5. Dispara a pendência para o Financeiro
      try {
        await financeiroIntegracaoService.triggerPendingTransaction({
          origem: "pdv_sales",
          origemId: createdSale.id,
          snapshot: createdSale,
          dataSugerida: new Date().toISOString().split("T")[0],
          valorSugeridoCentavos: Math.round(grandTotal * 100),
          descricaoSugerida: `Venda Showroom ${orderNumber} - Cliente: ${customerName}`,
          tipoPendencia: "entrada"
        });
      } catch (errFin) {
        console.warn("Erro ao gerar pendência financeira para venda PDV:", errFin);
      }

      showNotification && showNotification(`Venda ${orderNumber} finalizada com sucesso!`, "success");
      clearCheckoutForm();
    } catch (err) {
      console.error("Erro ao finalizar venda:", err);
      let errorDetail = err.message || String(err);
      
      if (err.data && err.data.data) {
        const fieldErrors = Object.entries(err.data.data)
          .map(([field, detail]) => {
            const detailMsg = detail.message || JSON.stringify(detail);
            return `${field}: ${detailMsg}`;
          })
          .join(" | ");
        if (fieldErrors) {
          errorDetail += ` (Campos: ${fieldErrors})`;
        }
      } else if (err.data && typeof err.data === "object" && err.data.message) {
        errorDetail += ` (${err.data.message})`;
      }
      
      showNotification && showNotification(`Erro ao finalizar: ${errorDetail}`, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- PARSE DE ENDEREÇO EM STRING ---
  const parseAddressString = (addressStr) => {
    const defaults = { street: "", number: "", complemento: "", district: "", city: "", statecode: "", zip: "" };
    if (!addressStr) return defaults;
    
    try {
      const cleanStr = addressStr.replace(/^\[RETIRADA\]\s*/i, "");
      
      const cepMatch = cleanStr.match(/CEP:\s*([0-9\-_]+)/i);
      const zip = cepMatch ? cepMatch[1].trim() : "";
      
      const stateMatch = cleanStr.match(/-\s*([A-Z]{2})/);
      const statecode = stateMatch ? stateMatch[1].trim().toUpperCase() : "";
      
      const numberMatch = cleanStr.match(/Nº\s*([^,]+)/i);
      const number = numberMatch ? numberMatch[1].trim() : "";
      
      const districtMatch = cleanStr.match(/Bairro\s*([^,.]+)/i);
      const district = districtMatch ? districtMatch[1].trim() : "";
      
      const parts = cleanStr.split(",");
      const street = parts[0] ? parts[0].trim() : "";
      
      let city = "";
      const cityPart = parts.find(p => p.includes(" - ") || p.includes("-"));
      if (cityPart) {
        const cityMatch = cityPart.match(/Bairro\s*[^,]+,\s*([^,-]+)/i) || cityPart.match(/,\s*([^,-]+)/) || cityPart.match(/^([^,-]+)/);
        city = cityMatch ? cityMatch[1].trim() : "";
      }
      
      return { street, number, district, city, statecode, zip };
    } catch (e) {
      console.error("Error parsing address:", e);
      return defaults;
    }
  };

  // --- ABRIR MODAL DE EMISSÃO DE NOTA FISCAL (NFe) ---
  const handleOpenInvoiceModal = async (sale) => {
    // 1. Tentar carregar a primeira reserva desta venda para obter dados do endereço estruturados se possível
    let structuredAddress = null;
    let shippingPriceVal = 0;
    
    const firstResId = sale.items?.[0]?.reservationIds?.[0];
    if (firstResId) {
      try {
        const res = await pb.collection("reservations").getOne(firstResId);
        if (res && res.shipping) {
          structuredAddress = res.shipping.address;
          shippingPriceVal = parseFloat(res.shipping.price) || 0;
        }
      } catch (err) {
        console.warn("Não foi possível carregar a reserva para obter endereço estruturado, usando fallback de parser:", err);
      }
    }
    
    // Fallback de parser se a reserva não foi carregada ou não tem endereço
    if (!structuredAddress) {
      const addrData = parseAddressString(sale.customer_address);
      structuredAddress = {
        destinatario: sale.customer_name,
        street: addrData.street,
        number: addrData.number,
        complemento: addrData.complemento,
        city: addrData.city,
        statecode: addrData.statecode,
        zip: addrData.zip,
        district: addrData.district
      };
      shippingPriceVal = sale.total - sale.subtotal + sale.discount;
    }

    const orderGroupForInvoice = {
      orderNumber: sale.order_number,
      customerName: sale.customer_name,
      totalValue: sale.total,
      items: sale.items.map((it, idx) => ({
        id: sale.id + "-" + idx, // Unique dummy id
        sku: it.sku,
        name: it.name,
        price: it.price,
        quantity: it.quantity,
        specs: it.specs,
        order: {
          customer: {
            name: sale.customer_name,
            cpf: sale.customer_cpf.replace(/\D/g, ""),
            cnpj: "",
            email: sale.customer_email,
            phone: sale.customer_phone,
          }
        },
        shipping: {
          price: shippingPriceVal,
          tipoenvio: sale.customer_address.includes("[RETIRADA]") ? "Retirada" : "Entrega",
          address: structuredAddress
        }
      }))
    };

    setSelectedSaleForInvoice(orderGroupForInvoice);
    setIsInvoiceModalOpen(true);
  };

  // --- GERAR ETIQUETA DOS CORREIOS ---
  const handleGenerateCorreiosLabel = async (sale) => {
    setLabelLoadingSaleId(sale.id);
    try {
      // 1. Tentar carregar a reserva da venda para pegar o endereço estruturado
      let structuredAddress = null;
      let shippingMethodCode = "03298"; // PAC por padrão
      
      const firstResId = sale.items?.[0]?.reservationIds?.[0];
      if (firstResId) {
        try {
          const res = await pb.collection("reservations").getOne(firstResId);
          if (res && res.shipping) {
            structuredAddress = res.shipping.address;
            if (res.shipping.tipoenvio === "SEDEX") {
              shippingMethodCode = "03220";
            }
          }
        } catch (err) {
          console.warn("Não foi possível carregar a reserva:", err);
        }
      }
      
      // Fallback de parser
      if (!structuredAddress) {
        const addrData = parseAddressString(sale.customer_address);
        structuredAddress = {
          destinatario: sale.customer_name,
          street: addrData.street,
          number: addrData.number,
          complemento: addrData.complemento,
          city: addrData.city,
          statecode: addrData.statecode,
          zip: addrData.zip,
          district: addrData.district
        };
      }

      if (!structuredAddress.zip || !structuredAddress.street || !structuredAddress.city || !structuredAddress.statecode) {
        throw new Error("Endereço do cliente incompleto para postagem nos Correios.");
      }

      // Busca a NF-e autorizada vinculada à venda do PDV
      const invoiceRes = await pb.collection("fiscal_invoices").getList(1, 1, {
        filter: `order_number = "${sale.order_number}" && nfe_status = "autorizada"`,
        sort: "-created",
      });
      const activeInvoice = invoiceRes.items[0] || null;
      if (!activeInvoice?.nfe_chave) {
        throw new Error(`A venda #${sale.order_number} ainda não possui uma NF-e autorizada. Emita a Nota Fiscal antes de gerar a etiqueta dos Correios.`);
      }

      const skus = sale.items.map(it => it.sku).join(", ");
      const dadosPedido = {
        codigoServico: shippingMethodCode,
        chaveNFe: activeInvoice.nfe_chave,
        numeroNotaFiscal: activeInvoice.nfe_numero || "",
        destinatario: {
          nome: sale.customer_name,
          cpfCnpj: sale.customer_cpf.replace(/\D/g, ""),
          dddCelular: sale.customer_phone.replace(/\D/g, "").substring(0, 2) || "11",
          celular: sale.customer_phone.replace(/\D/g, "").substring(2) || "999999999",
          email: sale.customer_email || "cliente@email.com",
          endereco: {
            cep: structuredAddress.zip.replace(/\D/g, ""),
            logradouro: structuredAddress.street,
            number: structuredAddress.number || "SN",
            complemento: structuredAddress.complemento || "",
            bairro: structuredAddress.district || "Centro",
            cidade: structuredAddress.city,
            uf: structuredAddress.statecode.toUpperCase()
          }
        },
        valorDeclaracao: String(sale.total.toFixed(2)),
        conteudoDeclaracao: skus.substring(0, 40) || "Joia em prata 925",
        ar: false,
        seguro: false,
        valorSegurado: 0
      };

      showNotification && showNotification("Autenticando nos Correios e enviando pré-postagem...", "info");
      const result = await correiosService.criarPrePostagem(dadosPedido);
      const prepostagemId = result.id;
      const codigoObjeto = result.codigoObjeto;

      showNotification && showNotification(`Pré-postagem criada! Código: ${codigoObjeto}. Gerando rótulo...`, "info");
      const idRecibo = await correiosService.gerarRotulo(prepostagemId);

      showNotification && showNotification("Processando rótulo com os Correios...", "info");
      const base64Pdf = await correiosService.baixarRotulo(idRecibo);

      showNotification && showNotification("Abrindo etiqueta PDF...", "success");
      await correiosService.abrirEtiquetaPDF(base64Pdf);

      await logAction(
        user,
        "PDV",
        "ETIQUETA_GERADA",
        `Etiqueta de envio dos Correios (${codigoObjeto}) gerada no PDV para o pedido ${sale.order_number} por ${user.name}.`,
        { orderNumber: sale.order_number, codigoObjeto }
      );
    } catch (err) {
      console.error(err);
      showNotification && showNotification(`Erro ao gerar etiqueta: ${err.message}`, "error");
    } finally {
      setLabelLoadingSaleId(null);
    }
  };

  // --- SINCRONIZAÇÃO MANUAL ---
  const handleSyncSale = async (sale) => {
    try {
      await pb.collection("pdv_sales").update(sale.id, {
        status: "PDV_SINCRONIZADO",
        synced: true,
      });
      await logAction(
        user,
        "PDV",
        "SINCRONIZAR",
        `Venda ${sale.order_number} marcada como sincronizada com o site por ${user.name}.`,
        { saleId: sale.id, orderNumber: sale.order_number }
      );
      showNotification && showNotification("Venda marcada como sincronizada!", "success");
    } catch (err) {
      console.error(err);
      showNotification && showNotification("Erro ao sincronizar.", "error");
    }
  };

  // --- ARQUIVAR VENDA ---
  const handleArchiveSale = async (sale) => {
    try {
      await pb.collection("pdv_sales").update(sale.id, {
        status: "PDV_ARQUIVADO",
        archived: true,
      });
      await logAction(
        user,
        "PDV",
        "ARQUIVAR",
        `Venda ${sale.order_number} arquivada por ${user.name}.`,
        { saleId: sale.id, orderNumber: sale.order_number }
      );
      showNotification && showNotification("Venda arquivada com sucesso.", "success");
    } catch (err) {
      console.error(err);
      showNotification && showNotification("Erro ao arquivar.", "error");
    }
  };

  // --- REVERSÃO (ESTORNO) ---
  const handleRevertSale = async (sale) => {
    if (!window.confirm(`Tem certeza de que deseja ESTORNAR e REVERTER a venda ${sale.order_number}?`)) {
      return;
    }

    try {
      const items = sale.items || [];

      for (let i = 0; i < items.length; i++) {
        const item = items[i];

        // 1. Devolver estoque físico
        if (item.source === "estoque" && item.soldItemIds && item.soldItemIds.length > 0) {
          await Promise.all(
            item.soldItemIds.map((id) =>
              pb.collection("inventory_items").update(id, {
                status: "in_stock",
                soldBy: "",
                dateOut: null,
              })
            )
          );
        }

        // 2. Deletar reservas de produção
        if (item.source === "producao" && item.reservationIds && item.reservationIds.length > 0) {
          await Promise.all(
            item.reservationIds.map(async (id) => {
              try {
                await pb.collection("reservations").delete(id);
              } catch (e) {
                console.warn(`Reserva ${id} não pôde ser apagada.`, e);
              }
            })
          );
        }
      }

      await pb.collection("pdv_sales").delete(sale.id);

      await logAction(
        user,
        "PDV",
        "REVERTER",
        `Venda Showroom ${sale.order_number} estornada por ${user.name}.`,
        { orderNumber: sale.order_number, total: sale.total }
      );

      showNotification && showNotification("Venda estornada e estoque devolvido!", "success");
    } catch (err) {
      console.error("Erro ao reverter venda:", err);
      showNotification && showNotification(`Erro ao reverter: ${err.message}`, "error");
    }
  };

  // --- DASHBOARD DATA ---
  const dashboardData = useMemo(() => {
    const start = new Date(dashStartDate + "T00:00:00");
    const end = new Date(dashEndDate + "T23:59:59");

    const filtered = sales.filter((sale) => {
      const date = new Date(sale.created);
      return date >= start && date <= end;
    });

    let faturamento = 0;
    let totalItens = 0;
    const pagamentos = {};
    const skusVendidos = {};
    const vendasDiarias = {};

    const diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    if (diffDays <= 45) {
      const temp = new Date(start);
      while (temp <= end) {
        const key = temp.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
        vendasDiarias[key] = 0;
        temp.setDate(temp.getDate() + 1);
      }
    }

    filtered.forEach((sale) => {
      faturamento += sale.total;
      const method = sale.payment_method || "outro";
      pagamentos[method] = (pagamentos[method] || 0) + sale.total;

      const dateKey = new Date(sale.created).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
      });
      vendasDiarias[dateKey] = (vendasDiarias[dateKey] || 0) + sale.total;

      const items = sale.items || [];
      items.forEach((item) => {
        totalItens += item.quantity;
        const sku = item.sku.toUpperCase();
        skusVendidos[sku] = (skusVendidos[sku] || 0) + item.quantity;
      });
    });

    const ticketMedio = filtered.length > 0 ? faturamento / filtered.length : 0;

    const pagamentosList = Object.entries(pagamentos).map(([key, val]) => {
      const methodInfo = PAYMENT_METHODS.find((pm) => pm.id === key) || { name: key };
      return { name: methodInfo.name, value: val };
    }).sort((a, b) => b.value - a.value);

    const skusList = Object.entries(skusVendidos)
      .map(([key, val]) => ({ sku: key, qty: val }))
      .sort((a, b) => b.qty - a.qty)
      .slice(0, 5);

    const trendData = Object.entries(vendasDiarias).map(([date, total]) => ({
      date,
      Total: total,
    }));

    return {
      faturamento,
      vendasCount: filtered.length,
      totalItens,
      ticketMedio,
      pagamentosList,
      skusList,
      trendData,
    };
  }, [sales, dashStartDate, dashEndDate]);

  const setDashPeriodRange = (days) => {
    const end = new Date();
    const start = new Date();
    if (days === "month") {
      start.setDate(1);
    } else {
      start.setDate(end.getDate() - Number(days));
    }
    setDashStartDate(start.toISOString().split("T")[0]);
    setDashEndDate(end.toISOString().split("T")[0]);
    setDashPeriod(days);
  };

  const pendingSales = useMemo(() => sales.filter((s) => s.status === "PDV_PENDENTE"), [sales]);
  const syncedSales = useMemo(() => sales.filter((s) => s.status === "PDV_SINCRONIZADO"), [sales]);

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] w-full bg-slate-50 overflow-hidden font-sans">
      
      {/* ─── BANNER / SUB-NAV ─── */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 shrink-0 flex flex-col sm:flex-row gap-3 justify-between items-center shadow-sm z-20">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-600 text-white rounded-xl flex items-center justify-center shadow-md">
            <ShoppingBag size={20} />
          </div>
          <div>
            <h1 className="text-lg font-extrabold text-slate-800 leading-tight">PDV Showroom</h1>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Frente de Caixa Presencial</p>
          </div>
        </div>

        <div className="flex bg-slate-100 p-1 rounded-xl w-full sm:w-auto">
          <button
            onClick={() => setActiveSubtab("vender")}
            className={`flex-1 sm:flex-none px-4 py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 ${
              activeSubtab === "vender" ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-800 hover:bg-slate-50/50"
            }`}
          >
            <ShoppingCart size={14} /> Caixa
          </button>
          <button
            onClick={() => setActiveSubtab("historico")}
            className={`flex-1 sm:flex-none px-4 py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 ${
              activeSubtab === "historico" ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-800 hover:bg-slate-50/50"
            }`}
          >
            <History size={14} /> Pendentes ({pendingSales.length})
          </button>
          <button
            onClick={() => setActiveSubtab("sincronizados")}
            className={`flex-1 sm:flex-none px-4 py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 ${
              activeSubtab === "sincronizados" ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-800 hover:bg-slate-50/50"
            }`}
          >
            <CheckCircle size={14} /> Sincronizados ({syncedSales.length})
          </button>
          <button
            onClick={() => setActiveSubtab("dashboard")}
            className={`flex-1 sm:flex-none px-4 py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 ${
              activeSubtab === "dashboard" ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-800 hover:bg-slate-50/50"
            }`}
          >
            <TrendingUp size={14} /> Dash
          </button>
        </div>
      </div>

      {/* ─── CAIXA TAB (VENDER) ─── */}
      {activeSubtab === "vender" && (
        <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
          
          {/* PAINEL ESQUERDO (Busca de Produtos e Resultados) */}
          <div className="flex-1 flex flex-col overflow-hidden p-4 space-y-4 border-r border-slate-200 text-left font-bold text-xs text-slate-700">
            
            {/* Bloco de Busca */}
            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm shrink-0">
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">
                Pesquisar Catálogo
              </label>
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input
                  type="text"
                  placeholder="Pesquise por SKU (ex: AN-TRI) ou Nome do produto..."
                  className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-700 text-sm outline-none focus:border-blue-500 focus:bg-white transition-all shadow-inner"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            {/* Listagem de Resultados da Busca (Como Consulta de Estoque) */}
            <div className="flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col overflow-hidden">
              <div className="bg-slate-50 px-4 py-3 border-b flex justify-between items-center shrink-0">
                <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">
                  Resultados Encontrados ({searchResults.length})
                </span>
                <button
                  type="button"
                  onClick={() => {
                    const cleanQuery = searchQuery.replace(/^PDV-/i, "").trim();
                    setNewProductData({
                      sku: cleanQuery,
                      name: "",
                      stoneColor: "Branca",
                      jewelryType: "",
                      material: "",
                      category: "",
                      price: "",
                    });
                    setIsRegisteringProduct(true);
                  }}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-extrabold text-[10px] rounded-lg shadow-md transition-all uppercase active:scale-95 flex items-center gap-1"
                >
                  <Plus size={10} /> Cadastrar Produto
                </button>
              </div>

              <div className="flex-1 overflow-y-auto divide-y divide-slate-100 custom-scrollbar">
                {searchQuery.trim() === "" ? (
                  <div className="h-full flex flex-col justify-center items-center text-slate-400 p-8">
                    <Compass size={44} className="opacity-15 mb-2" />
                    <p className="text-xs font-semibold">Digite algo no campo acima para pesquisar.</p>
                    <p className="text-[10px] text-slate-500 mt-1">
                      A pesquisa busca em tempo real em todo o catálogo físico e variações.
                    </p>
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="h-full flex flex-col justify-center items-center text-slate-400 p-8">
                    <AlertCircle size={44} className="opacity-15 mb-2 text-amber-500" />
                    <p className="text-xs font-semibold">Nenhum produto correspondente encontrado.</p>
                    <button
                      type="button"
                      onClick={() => {
                        const cleanQuery = searchQuery.replace(/^PDV-/i, "").trim();
                        setNewProductData({
                          sku: cleanQuery,
                          name: "",
                          stoneColor: "Branca",
                          jewelryType: "",
                          material: "",
                          category: "",
                          price: "",
                        });
                        setIsRegisteringProduct(true);
                      }}
                      className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-extrabold text-xs rounded-xl shadow-md transition-all uppercase flex items-center gap-1.5 active:scale-95"
                    >
                      <Plus size={12} /> Cadastrar "{searchQuery}" no Catálogo
                    </button>
                  </div>
                ) : (
                  <div className="p-3.5 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 gap-3.5">
                    {searchResults.map((product) => {
                      const stockCount = getSkuStockCount(product.sku);
                      return (
                        <div
                          key={product.id || product.sku}
                          className="bg-white border border-slate-200 hover:border-blue-400 hover:shadow-md rounded-2xl p-3.5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3.5 transition-all group"
                        >
                          {/* Imagem + Textos */}
                          <div className="flex items-center gap-3.5 min-w-0 flex-1 w-full">
                            {product.image || product.image_url ? (
                              <img
                                src={product.image || product.image_url}
                                alt={product.sku}
                                className="w-16 h-16 object-cover rounded-xl border border-slate-200 bg-slate-50 shrink-0 shadow-sm p-0.5 group-hover:scale-105 transition-transform"
                              />
                            ) : (
                              <div className="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center border border-slate-200 font-black text-xs text-slate-400 shrink-0 font-mono">
                                {product.sku.slice(0, 4)}
                              </div>
                            )}

                            <div className="flex-1 min-w-0 space-y-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-black text-slate-900 text-xs font-mono tracking-wide bg-slate-100 px-2 py-0.5 rounded-md border border-slate-200" title={product.sku}>
                                  {product.sku}
                                </span>
                                {stockCount > 0 ? (
                                  <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-md text-[9px] font-black uppercase whitespace-nowrap">
                                    {stockCount} em estoque
                                  </span>
                                ) : (
                                  <span className="bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-md text-[9px] font-black uppercase whitespace-nowrap">
                                    Sob Produção
                                  </span>
                                )}
                              </div>

                              <p className="text-xs text-slate-600 font-semibold line-clamp-2 leading-snug" title={product.name}>
                                {product.name}
                              </p>

                              <div className="pt-0.5">
                                <span className="font-black text-blue-600 text-sm">
                                  {formatMoney(product.price || product.full_data?.price)}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Botão de Ação */}
                          <div className="shrink-0 w-full sm:w-auto flex sm:flex-col justify-end">
                            <button
                              onClick={() => handleOpenConfigure(product)}
                              className="w-full sm:w-auto px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-extrabold text-xs rounded-xl shadow-md shadow-blue-200 hover:shadow-lg transition-all uppercase active:scale-95 flex items-center justify-center gap-1.5 whitespace-nowrap"
                            >
                              <Plus size={14} /> Adicionar
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Carrinho de Compras - Agora abaixo dos resultados de busca no painel esquerdo */}
            <div className="bg-slate-100 rounded-2xl border border-slate-200 p-4 shrink-0 flex flex-col h-[220px]">
              <div className="flex justify-between items-center border-b border-slate-300 pb-2 mb-2">
                <span className="text-[10px] font-extrabold text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
                  <ShoppingCart size={14} className="text-slate-600" /> Carrinho ({cart.length})
                </span>
                {cart.length > 0 && (
                  <button onClick={() => setCart([])} className="text-[9px] font-black text-rose-600 bg-white hover:bg-rose-50 border border-slate-200 px-2 py-0.5 rounded shadow-sm">
                    Limpar Tudo
                  </button>
                )}
              </div>

              <div className="flex-1 overflow-y-auto space-y-2 pr-1 custom-scrollbar">
                {cart.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-slate-400 text-xs italic">
                    Nenhum item adicionado ao carrinho.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {cart.map((item, index) => (
                      <div key={index} className="bg-white border border-slate-200 rounded-xl p-3 flex items-center justify-between gap-3 shadow-sm">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-extrabold text-slate-800 text-xs truncate">{item.sku}</span>
                            <span className={`text-[8px] px-1.5 py-0.5 rounded-md font-black border uppercase ${
                              item.source === "estoque" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-purple-50 text-purple-700 border-purple-200"
                            }`}>
                              {item.source === "estoque" ? "Estoque" : "Produção"}
                            </span>
                          </div>
                          {/* Resumo compacto de especificações */}
                          <div className="text-[10px] text-slate-500 font-bold truncate mt-1">
                            {item.specs.size && `Aro: ${item.specs.size} • `}
                            {item.specs.stoneColor && `Pedra: ${item.specs.stoneColor} • `}
                            {item.specs.finishing && `Banho: ${item.specs.finishing}`}
                            {item.specs.engraving && (
                              <span className="inline-block ml-1">
                                <EngravingDisplay rawText={item.specs.engraving} mode="compact" />
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Qtd e Ações */}
                        <div className="flex items-center gap-3 shrink-0">
                          <div className="text-right flex flex-col">
                            <span className="text-[10px] text-slate-400 font-medium">Qtd: {item.quantity}</span>
                            <span className="text-slate-800 font-black text-xs">{formatMoney(item.price * item.quantity)}</span>
                          </div>

                          <div className="flex gap-1">
                            <button
                              onClick={() => handleOpenEditCartItem(index)}
                              className="p-1.5 hover:bg-blue-50 rounded-lg text-blue-600 border border-slate-100 bg-slate-50"
                              title="Editar especificações"
                            >
                              <Edit size={12} />
                            </button>
                            <button
                              onClick={() => removeFromCart(index)}
                              className="p-1.5 hover:bg-rose-50 rounded-lg text-rose-500 border border-slate-100 bg-slate-50"
                              title="Remover"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

          </div>

          {/* PAINEL DIREITO (Checkout e Totais) */}
          <div className="w-full md:w-80 lg:w-96 flex flex-col bg-white overflow-y-auto border-l border-slate-200 p-4 space-y-4 shrink-0 shadow-lg relative custom-scrollbar font-bold text-xs text-slate-700 text-left">
            
            {/* Alternador de Modo de Envio (Retirada vs Entrega) */}
            <div className="flex bg-slate-100 p-0.5 rounded-xl border shrink-0">
              <button
                type="button"
                onClick={() => setSaleMode("retirada")}
                className={`flex-1 py-2 text-[10px] font-black rounded-lg transition-all flex items-center justify-center gap-1.5 ${
                  saleMode === "retirada" ? "bg-white text-blue-700 shadow-sm border border-slate-200/50" : "text-slate-500 hover:text-slate-800"
                }`}
              >
                <ShoppingBag size={13} /> Retirada (Compra na Hora)
              </button>
              <button
                type="button"
                onClick={() => setSaleMode("entrega")}
                className={`flex-1 py-2 text-[10px] font-black rounded-lg transition-all flex items-center justify-center gap-1.5 ${
                  saleMode === "entrega" ? "bg-white text-blue-700 shadow-sm border border-slate-200/50" : "text-slate-500 hover:text-slate-800"
                }`}
              >
                <Truck size={13} /> Pedido para Entrega
              </button>
            </div>

            {/* Identificação da Venda e Cadastro (Grid multi-colunas compacto para Tablet) */}
            <div className="bg-slate-50 p-3.5 rounded-2xl border border-slate-200/80 space-y-3 font-semibold text-[11px] text-slate-700 shrink-0">
              <div className="flex items-center justify-between border-b pb-2 border-slate-200">
                <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest flex items-center gap-1">
                  <User size={13} /> Identificação & Cadastro
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => {
                      setCustomerName("Consumidor Final");
                      setCustomerCpf("999.999.999-99");
                      setCustomerPhone("(11) 99999-9999");
                      setCustomerEmail("consumidor@semprejoias.com.br");
                      setCustomerCep("03533-001");
                      setAddressStreet("Av Waldemar Carlos Pereira");
                      setAddressNumber("179");
                      setAddressDistrict("Vila Matilde");
                      setAddressCity("São Paulo");
                      setAddressState("SP");
                      showNotification && showNotification("Dados de Consumidor preenchidos!", "success");
                    }}
                    className="text-[9px] bg-white hover:bg-slate-100 text-slate-600 font-bold px-2 py-0.5 rounded shadow-sm border border-slate-250 transition-colors"
                  >
                    Cliente Balcão
                  </button>
                  <span className="text-[9px] bg-blue-100 text-blue-800 font-bold px-2 py-0.5 rounded">
                    LJ01
                  </span>
                </div>
              </div>

              {/* Grid Compacto de Campos */}
              <div className="grid grid-cols-2 gap-2">
                {/* Nome do Cliente */}
                <div className="col-span-2 space-y-0.5">
                  <label className="block text-[9px] text-slate-400 font-bold uppercase">
                    Nome do Cliente <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Nome do cliente"
                    className="w-full px-2.5 py-1.5 border rounded-lg bg-white outline-none focus:border-blue-500 text-[11px] font-bold"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                  />
                </div>

                {/* CPF */}
                <div className="space-y-0.5">
                  <label className="block text-[9px] text-slate-400 font-bold uppercase">
                    CPF <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="000.000.000-00"
                    className="w-full px-2.5 py-1.5 border rounded-lg bg-white outline-none focus:border-blue-500 text-[11px] font-mono font-bold"
                    value={customerCpf}
                    onChange={(e) => setCustomerCpf(e.target.value)}
                  />
                </div>

                {/* Telefone */}
                <div className="space-y-0.5">
                  <label className="block text-[9px] text-slate-400 font-bold uppercase">
                    Telefone / Zap <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="(00) 00000-0000"
                    className="w-full px-2.5 py-1.5 border rounded-lg bg-white outline-none focus:border-blue-500 text-[11px] font-bold"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                  />
                </div>

                {/* E-mail */}
                <div className="space-y-0.5">
                  <label className="block text-[9px] text-slate-400 font-bold uppercase">
                    E-mail <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="email"
                    placeholder="cliente@email.com"
                    className="w-full px-2.5 py-1.5 border rounded-lg bg-white outline-none focus:border-blue-500 text-[11px] font-bold"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                  />
                </div>

                {/* CEP */}
                <div className="space-y-0.5">
                  <label className="block text-[9px] text-slate-400 font-bold uppercase">
                    CEP {saleMode === "entrega" && <span className="text-rose-500">*</span>}
                  </label>
                  <input
                    type="text"
                    maxLength={9}
                    placeholder="00000-000"
                    className="w-full px-2.5 py-1.5 border rounded-lg bg-white outline-none focus:border-blue-500 text-[11px] font-mono font-bold"
                    value={customerCep}
                    onChange={(e) => setCustomerCep(e.target.value)}
                  />
                </div>

                {/* Logradouro / Rua */}
                <div className="col-span-2 space-y-0.5">
                  <label className="block text-[9px] text-slate-400 font-bold uppercase">
                    Logradouro / Rua {saleMode === "entrega" && <span className="text-rose-500">*</span>}
                  </label>
                  <input
                    type="text"
                    placeholder="Rua, Avenida..."
                    className="w-full px-2.5 py-1.5 border rounded-lg bg-white outline-none focus:border-blue-500 text-[11px]"
                    value={addressStreet}
                    onChange={(e) => setAddressStreet(e.target.value)}
                  />
                </div>

                {/* Número */}
                <div className="space-y-0.5">
                  <label className="block text-[9px] text-slate-400 font-bold uppercase">
                    Número {saleMode === "entrega" && <span className="text-rose-500">*</span>}
                  </label>
                  <input
                    type="text"
                    placeholder="Nº"
                    className="w-full px-2.5 py-1.5 border rounded-lg bg-white outline-none focus:border-blue-500 text-[11px] font-bold"
                    value={addressNumber}
                    onChange={(e) => setAddressNumber(e.target.value)}
                  />
                </div>

                {/* Bairro */}
                <div className="space-y-0.5">
                  <label className="block text-[9px] text-slate-400 font-bold uppercase">Bairro</label>
                  <input
                    type="text"
                    placeholder="Bairro"
                    className="w-full px-2.5 py-1.5 border rounded-lg bg-white outline-none focus:border-blue-500 text-[11px]"
                    value={addressDistrict}
                    onChange={(e) => setAddressDistrict(e.target.value)}
                  />
                </div>

                {/* Cidade / Estado */}
                <div className="col-span-2 grid grid-cols-3 gap-2">
                  <div className="col-span-2 space-y-0.5">
                    <label className="block text-[9px] text-slate-400 font-bold uppercase">
                      Cidade {saleMode === "entrega" && <span className="text-rose-500">*</span>}
                    </label>
                    <input
                      type="text"
                      placeholder="Cidade"
                      className="w-full px-2.5 py-1.5 border rounded-lg bg-white outline-none focus:border-blue-500 text-[11px]"
                      value={addressCity}
                      onChange={(e) => setAddressCity(e.target.value)}
                    />
                  </div>
                  <div className="space-y-0.5">
                    <label className="block text-[9px] text-slate-400 font-bold uppercase">
                      UF {saleMode === "entrega" && <span className="text-rose-500">*</span>}
                    </label>
                    <input
                      type="text"
                      placeholder="UF"
                      className="w-full px-2.5 py-1.5 border rounded-lg bg-white outline-none focus:border-blue-500 text-[11px] font-bold uppercase"
                      value={addressState}
                      onChange={(e) => setAddressState(e.target.value)}
                    />
                  </div>
                </div>

                {/* Complemento */}
                <div className="space-y-0.5">
                  <label className="block text-[9px] text-slate-400 font-bold uppercase">Complemento</label>
                  <input
                    type="text"
                    placeholder="Apto, Bloco..."
                    className="w-full px-2.5 py-1.5 border rounded-lg bg-white outline-none focus:border-blue-500 text-[11px]"
                    value={addressComplement}
                    onChange={(e) => setAddressComplement(e.target.value)}
                  />
                </div>

                {/* Nº do Pedido */}
                <div className="space-y-0.5">
                  <label className="block text-[9px] text-slate-400 font-bold uppercase">Nº do Pedido</label>
                  <input
                    type="text"
                    className="w-full px-2.5 py-1.5 border rounded-lg bg-white outline-none focus:border-blue-500 font-mono text-[11px] font-bold"
                    value={orderNumber}
                    onChange={(e) => setOrderNumber(e.target.value)}
                  />
                </div>
              </div>

              {/* Observações do Pedido */}
              <div className="space-y-1 pt-1">
                <label className="block text-[9px] text-slate-400 font-bold uppercase">Observações do Pedido</label>
                <textarea
                  placeholder="Instruções de envio ou observações..."
                  className="w-full p-2 border rounded-lg bg-white outline-none focus:border-blue-500 text-[11px] h-10 resize-none font-medium"
                  value={orderNotes}
                  onChange={(e) => setOrderNotes(e.target.value)}
                />
              </div>
            </div>

            {/* Cálculo de Frete Rápido (Exibido apenas no modo de Entrega) */}
            {saleMode === "entrega" && (
              <div className="bg-slate-50 p-3.5 rounded-2xl border border-slate-200/80 space-y-3 font-semibold text-[11px] text-slate-700 shrink-0 animate-fade-in">
                <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest flex items-center gap-1.5 border-b pb-2 border-slate-200">
                  <Truck size={14} /> Cálculo Rápido de Frete
                </span>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <label className="block text-[9px] text-slate-400 font-bold uppercase">Tipo de Envio</label>
                    <select
                      value={shippingMethod}
                      onChange={(e) => setShippingMethod(e.target.value)}
                      className="w-full p-2 border rounded-lg bg-white outline-none focus:border-blue-500 text-xs font-bold"
                    >
                      <option value="pac">PAC (Correios)</option>
                      <option value="sedex">SEDEX (Correios)</option>
                      <option value="motoboy">Motoboy</option>
                      <option value="outro">Outro / Customizado</option>
                    </select>
                  </div>

                  <div className="space-y-1">
                    <label className="block text-[9px] text-slate-400 font-bold uppercase">Valor do Frete (R$)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      className="w-full p-2 border rounded-lg bg-white outline-none focus:border-blue-500 text-xs font-bold"
                      value={shippingPrice}
                      onChange={(e) => setShippingPrice(parseFloat(e.target.value) || 0)}
                    />
                  </div>
                </div>

                {addressState && (
                  <p className="text-[9px] text-slate-400 italic text-left leading-normal">
                    Estimativa local para {addressState}: PAC R$ {getEstimatedShipping(addressState, "pac").toFixed(2)} | SEDEX R$ {getEstimatedShipping(addressState, "sedex").toFixed(2)}
                  </p>
                )}
              </div>
            )}

            {/* Pagamento */}
            <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200/80 space-y-3 font-semibold text-[11px] text-slate-700 shrink-0">
              <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest flex items-center gap-1.5 border-b pb-2 border-slate-200">
                <CreditCard size={14} /> Forma de Pagamento
              </span>
              <div className="grid grid-cols-2 gap-2">
                {PAYMENT_METHODS.map((method) => {
                  const isSelected = paymentMethod === method.id;
                  return (
                    <button
                      key={method.id}
                      onClick={() => handleSelectPaymentMethod(method.id)}
                      className={`p-2 rounded-xl border text-center font-bold text-xs transition-all active:scale-95 flex items-center justify-center gap-1.5 ${
                        isSelected
                          ? `${method.color} shadow-sm ring-1 ring-blue-200 font-black`
                          : "border-slate-200 bg-white text-slate-500 hover:bg-slate-100/60"
                      }`}
                    >
                      {method.name}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Desconto */}
            <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200/80 space-y-3 font-semibold text-[11px] text-slate-700 shrink-0">
              <div className="flex items-center justify-between border-b pb-2 border-slate-200">
                <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                  <DollarSign size={14} /> Desconto Manual / Cupom (Máx 50%)
                </span>
                <div className="flex bg-white rounded-lg p-0.5 border text-[9px] font-bold">
                  <button
                    onClick={() => setDiscountType("percent")}
                    className={`px-2 py-0.5 rounded ${
                      discountType === "percent" ? "bg-blue-600 text-white" : "text-slate-500"
                    }`}
                  >
                    %
                  </button>
                  <button
                    onClick={() => setDiscountType("value")}
                    className={`px-2 py-0.5 rounded ${
                      discountType === "value" ? "bg-blue-600 text-white" : "text-slate-500"
                    }`}
                  >
                    R$
                  </button>
                </div>
              </div>

              <div className="flex gap-2">
                <input
                  type="number"
                  placeholder={discountType === "percent" ? "Ex: 10%" : "Ex: 50.00"}
                  className="flex-1 p-2 border rounded-lg bg-white outline-none focus:border-blue-500 text-xs font-bold"
                  value={discountValue}
                  onChange={(e) => setDiscountValue(e.target.value)}
                />
                <div className="flex gap-1 shrink-0">
                  {[5, 10, 15].map((val) => (
                    <button
                      key={val}
                      onClick={() => {
                        setDiscountType("percent");
                        setDiscountValue(val.toString());
                      }}
                      className="px-2.5 py-1.5 bg-white hover:bg-slate-200 border rounded-lg text-xs font-bold transition-colors shadow-sm"
                    >
                      {val}%
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Valor Adicional (Acréscimo) */}
            <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200/80 space-y-3 font-semibold text-[11px] text-slate-700 shrink-0">
              <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest flex items-center gap-1.5 border-b pb-2 border-slate-200">
                <Plus size={14} /> Valor Adicional (Acréscimo R$)
              </span>
              <input
                type="number"
                step="0.01"
                min="0"
                placeholder="Ex: 25.00"
                className="w-full p-2 border rounded-lg bg-white outline-none focus:border-blue-500 text-xs font-bold"
                value={additionalValue}
                onChange={(e) => setAdditionalValue(e.target.value)}
              />
            </div>

            {/* Totais */}
            <div className="bg-slate-900 text-white p-5 rounded-2xl border shadow-xl flex flex-col gap-3 font-bold text-xs tracking-tight mb-6 shrink-0">
              <div className="flex justify-between items-center opacity-70">
                <span>Subtotal:</span>
                <span>{formatMoney(subtotal)}</span>
              </div>
              
              {paymentDiscountAmount > 0 && (
                <div className="flex justify-between items-center text-teal-400">
                  <span>Desconto 10% ({paymentMethod === "pix" ? "PIX" : "Dinheiro"}):</span>
                  <span>- {formatMoney(paymentDiscountAmount)}</span>
                </div>
              )}

              {manualDiscountAmount > 0 && (
                <div className="flex justify-between items-center text-rose-400">
                  <span>Desconto Manual {discountType === "percent" ? `(${discountValue}%)` : ""}:</span>
                  <span>- {formatMoney(manualDiscountAmount)}</span>
                </div>
              )}

              {Number(shippingPrice) > 0 && (
                <div className="flex justify-between items-center text-amber-400">
                  <span>Frete ({shippingMethod.toUpperCase()}):</span>
                  <span>+ {formatMoney(Number(shippingPrice))}</span>
                </div>
              )}

              {Number(additionalValue) > 0 && (
                <div className="flex justify-between items-center text-blue-400">
                  <span>Acréscimo:</span>
                  <span>+ {formatMoney(Number(additionalValue))}</span>
                </div>
              )}
              
              <div className="h-[1px] bg-slate-800 my-1"></div>

              <div className="flex justify-between items-baseline">
                <span className="text-sm">Total Geral:</span>
                <span className="text-2xl font-black text-emerald-400">{formatMoney(grandTotal)}</span>
              </div>

              <button
                disabled={isSubmitting || cart.length === 0}
                onClick={handleCheckout}
                className="w-full mt-4 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-700 disabled:opacity-50 text-slate-900 font-extrabold text-sm py-4 rounded-xl shadow-lg transition-all active:scale-[0.98] flex items-center justify-center gap-2 uppercase tracking-wider"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 rounded-full border-2 border-slate-900 border-t-transparent animate-spin"></div>
                    Processando...
                  </>
                ) : (
                  <>Finalizar Venda</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── MODAL DE ESPECIFICAÇÕES DO PRODUTO (CONVERSÃO DE ITEM DO PDV) ─── */}
      {configuringProduct && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in text-left">
          <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden flex flex-col font-bold text-xs text-slate-700">
            
            {/* Header */}
            <div className="bg-blue-600 p-4 text-white flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Layers size={20} />
                <div>
                  <h3 className="font-extrabold text-sm uppercase tracking-wide">
                    {editingCartIndex !== null ? "Editar Especificações" : "Configurar Peça para Carrinho"}
                  </h3>
                  <p className="text-[10px] text-blue-100 font-mono mt-0.5">{configuringProduct.sku}</p>
                </div>
              </div>
              <button
                onClick={() => setConfiguringProduct(null)}
                className="hover:bg-white/10 p-1.5 rounded-full text-white"
              >
                <X size={18} />
              </button>
            </div>

            {/* Conteudo */}
            <div className="p-5 space-y-4 overflow-y-auto max-h-[75vh] custom-scrollbar">
              
              <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 text-[10px] font-bold text-slate-500 leading-relaxed uppercase">
                {configuringProduct.name || "Produto Sem Nome"}
                <div className="text-[11px] text-blue-600 font-black tracking-tight mt-0.5">
                  Valor: {formatMoney(configuringProduct.price || configuringProduct.full_data?.price)}
                </div>
              </div>

              {/* Origem, Preço e Quantidade */}
              <div className="grid grid-cols-3 gap-4">
                
                <div className="space-y-1">
                  <label className="block text-[9px] text-slate-400 font-black uppercase">
                    Origem do Item <span className="text-red-500">*</span>
                  </label>
                  <select
                    className="w-full p-2.5 border rounded-lg bg-white outline-none focus:border-blue-500 text-xs font-bold shadow-sm"
                    value={configSource}
                    onChange={(e) => setConfigSource(e.target.value)}
                  >
                    <option value="estoque" disabled={getSkuStockCount(configuringProduct.sku) === 0}>
                      Estoque Físico ({getSkuStockCount(configuringProduct.sku)} Disp)
                    </option>
                    <option value="producao">Produção (Enviar para Fábrica)</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="block text-[9px] text-slate-400 font-black uppercase">
                    Preço Unitário (R$)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Ex: 150.00"
                    className="w-full p-2.5 border rounded-lg bg-white outline-none focus:border-blue-500 text-xs font-bold shadow-sm"
                    value={configPrice}
                    onChange={(e) => setConfigPrice(e.target.value)}
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-[9px] text-slate-400 font-black uppercase">Quantidade</label>
                  <div className="flex items-center border rounded-lg bg-white overflow-hidden shadow-sm">
                    <button
                      type="button"
                      onClick={() => setConfigQty(Math.max(1, configQty - 1))}
                      className="px-3 py-2 bg-slate-50 hover:bg-slate-100 text-slate-600 border-r active:scale-95"
                    >
                      <Minus size={12} />
                    </button>
                    <input
                      type="number"
                      min={1}
                      className="w-full text-center outline-none font-bold text-xs bg-transparent"
                      value={configQty}
                      onChange={(e) => {
                        const val = Math.max(1, parseInt(e.target.value) || 1);
                        if (configSource === "estoque") {
                          const stockCount = getSkuStockCount(configuringProduct.sku);
                          setConfigQty(Math.min(val, stockCount));
                        } else {
                          setConfigQty(val);
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const nextVal = configQty + 1;
                        if (configSource === "estoque") {
                          const stockCount = getSkuStockCount(configuringProduct.sku);
                          if (nextVal <= stockCount) setConfigQty(nextVal);
                        } else {
                          setConfigQty(nextVal);
                        }
                      }}
                      className="px-3 py-2 bg-slate-50 hover:bg-slate-100 text-slate-600 border-l active:scale-95"
                    >
                      <Plus size={12} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Especificações Físicas */}
              <div className="space-y-3 pt-3 border-t">
                <span className="block text-[10px] font-extrabold text-blue-600 uppercase tracking-widest mb-1">
                  Especificações da Joia
                </span>

                <div className="grid grid-cols-2 gap-3">
                  
                  {/* Aro */}
                  <div className="space-y-1">
                    <label className="block text-[9px] text-slate-400 uppercase font-black">Aro / Tamanho</label>
                    <input
                      type="text"
                      placeholder="Ex: 15"
                      className="w-full p-2.5 border rounded-lg bg-white outline-none focus:border-blue-500 font-bold text-xs"
                      value={configSpecs.size}
                      onChange={(e) => setConfigSpecs({ ...configSpecs, size: e.target.value })}
                    />
                  </div>

                  {/* Cor da Pedra */}
                  <div className="space-y-1">
                    <label className="block text-[9px] text-slate-400 uppercase font-black">Cor da Pedra</label>
                    <select
                      className="w-full p-2.5 border rounded-lg bg-white outline-none focus:border-blue-500 font-bold text-xs"
                      value={configSpecs.stoneColor}
                      onChange={(e) => setConfigSpecs({ ...configSpecs, stoneColor: e.target.value })}
                    >
                      {STONE_COLORS.map((color) => (
                        <option key={color} value={color}>{color}</option>
                      ))}
                    </select>
                  </div>

                  {/* Tipo de Pedra */}
                  <div className="space-y-1">
                    <label className="block text-[9px] text-slate-400 uppercase font-black">Tipo de Pedra</label>
                    <select
                      className="w-full p-2.5 border rounded-lg bg-white outline-none focus:border-blue-500 text-xs"
                      value={configSpecs.stoneType}
                      onChange={(e) => setConfigSpecs({ ...configSpecs, stoneType: e.target.value })}
                    >
                      {STONE_TYPES.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  {/* Banho / Finalização */}
                  <div className="space-y-1">
                    <label className="block text-[9px] text-slate-400 uppercase font-black">Banho / Finalização</label>
                    <input
                      type="text"
                      placeholder="Ex: Banho Ouro"
                      className="w-full p-2.5 border rounded-lg bg-white outline-none focus:border-blue-500 text-xs"
                      value={configSpecs.finishing}
                      onChange={(e) => setConfigSpecs({ ...configSpecs, finishing: e.target.value })}
                    />
                  </div>

                  {/* Lote da Pedra */}
                  <div className="space-y-1">
                    <label className="block text-[9px] text-slate-400 uppercase font-black">Lote da Pedra</label>
                    <input
                      type="text"
                      placeholder="Ex: Lote A"
                      className="w-full p-2.5 border rounded-lg bg-white outline-none focus:border-blue-500 font-mono text-xs"
                      value={configSpecs.stoneBatch}
                      onChange={(e) => setConfigSpecs({ ...configSpecs, stoneBatch: e.target.value })}
                    />
                  </div>

                  {/* Gravação */}
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <label className="block text-[9px] text-slate-400 uppercase font-black">Gravação Interna</label>
                      {(() => {
                        const count = (configSpecs.engraving || "").length;
                        const style = getEngravingBadgeStyle(count);
                        return (
                          <span
                            className={`text-[9px] px-1.5 py-0.2 rounded-full border transition-all ${
                              count > 0 ? style.badgeClass : "bg-slate-100 text-slate-400 border-slate-200"
                            }`}
                          >
                            <span className="font-extrabold">{count}</span> carac.
                          </span>
                        );
                      })()}
                    </div>
                    <input
                      type="text"
                      placeholder="Ex: Amor Eterno // Medalha 2: 25.12"
                      className="w-full p-2.5 border rounded-lg bg-white outline-none focus:border-blue-500 text-xs"
                      value={configSpecs.engraving}
                      onChange={(e) => setConfigSpecs({ ...configSpecs, engraving: e.target.value })}
                    />
                    {configSpecs.engraving && (
                      <div className="pt-1">
                        <EngravingDisplay rawText={configSpecs.engraving} mode="compact" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Exceção */}
                <div className="space-y-1">
                  <label className="block text-[9px] text-red-500 uppercase font-black flex items-center gap-1">
                    <AlertCircle size={11} /> Aviso de Exceção / Observação Especial
                  </label>
                  <input
                    type="text"
                    placeholder="Alteração no design da peça, acabamento diferente..."
                    className="w-full p-2.5 border border-red-100 bg-red-50/20 rounded-lg outline-none focus:border-red-400 text-xs text-red-800"
                    value={configSpecs.exception}
                    onChange={(e) => setConfigSpecs({ ...configSpecs, exception: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Ações */}
            <div className="p-4 border-t bg-slate-50 flex justify-end gap-3 shrink-0">
              <button
                type="button"
                onClick={() => setConfiguringProduct(null)}
                className="px-4 py-2 border rounded-xl hover:bg-slate-100 text-slate-500 font-bold"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmCartItem}
                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-extrabold flex items-center gap-1.5 shadow-md shadow-blue-100 active:scale-95 transition-all"
              >
                <Check size={16} /> Confirmar Item
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── MODAL DE CADASTRO RÁPIDO DE PRODUTO NO CATÁLOGO ─── */}
      {isRegisteringProduct && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in text-left">
          <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden flex flex-col font-bold text-xs text-slate-700">
            
            {/* Header */}
            <div className="bg-blue-600 p-4 text-white flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Plus size={20} />
                <div>
                  <h3 className="font-extrabold text-sm uppercase tracking-wide">
                    Cadastrar Novo Produto
                  </h3>
                  <p className="text-[10px] text-blue-100 mt-0.5">Lançamento rápido no catálogo</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsRegisteringProduct(false)}
                className="hover:bg-white/10 p-1.5 rounded-full text-white"
              >
                <X size={18} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleRegisterProduct} className="flex-1 flex flex-col overflow-hidden">
              <div className="p-5 space-y-4 overflow-y-auto max-h-[70vh] custom-scrollbar">
                
                {/* Alert/Aviso de Proteção do SKU */}
                <div className="bg-blue-50 border border-blue-200 text-blue-800 p-3 rounded-xl flex items-start gap-2">
                  <AlertCircle size={16} className="shrink-0 mt-0.5" />
                  <div className="text-[10px] font-medium leading-relaxed">
                    <b>Proteção de SKU Ativada:</b> O sistema adicionará automaticamente o prefixo <b>PDV-</b> ao SKU digitado para evitar a sobreposição de produtos oficiais do e-commerce.
                  </div>
                </div>

                {/* SKU e Nome */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-1 space-y-1">
                    <label className="block text-[9px] text-slate-400 font-black uppercase">
                      SKU <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 font-mono text-[10px] font-bold">PDV-</span>
                      <input
                        type="text"
                        required
                        placeholder="AN-01"
                        className="w-full pl-10 pr-2 py-2.5 border rounded-lg bg-white outline-none focus:border-blue-500 font-mono font-bold text-xs uppercase"
                        value={newProductData.sku.replace(/^PDV-/i, "")}
                        onChange={(e) => setNewProductData({ ...newProductData, sku: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="col-span-2 space-y-1">
                    <label className="block text-[9px] text-slate-400 font-black uppercase">
                      Nome do Produto <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="Ex: Anel Trindade Prata"
                      className="w-full p-2.5 border rounded-lg bg-white outline-none focus:border-blue-500 font-bold text-xs"
                      value={newProductData.name}
                      onChange={(e) => setNewProductData({ ...newProductData, name: e.target.value })}
                    />
                  </div>
                </div>

                {/* Cor Padrão e Preço */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="block text-[9px] text-slate-400 font-black uppercase">
                      Preço (R$) <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      required
                      placeholder="Ex: 199.90"
                      className="w-full p-2.5 border rounded-lg bg-white outline-none focus:border-blue-500 font-bold text-xs"
                      value={newProductData.price}
                      onChange={(e) => setNewProductData({ ...newProductData, price: e.target.value })}
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="block text-[9px] text-slate-400 font-black uppercase">Cor da Pedra</label>
                    <select
                      className="w-full p-2.5 border rounded-lg bg-white outline-none focus:border-blue-500 font-bold text-xs"
                      value={newProductData.stoneColor}
                      onChange={(e) => setNewProductData({ ...newProductData, stoneColor: e.target.value })}
                    >
                      {STONE_COLORS.map((color) => (
                        <option key={color} value={color}>{color}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Tipo de Joia, Material e Categoria */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <label className="block text-[9px] text-slate-400 font-black uppercase">Tipo de Joia</label>
                    <input
                      type="text"
                      list="quick-jewelry-types"
                      placeholder="Ex: Anel"
                      className="w-full p-2.5 border rounded-lg bg-white outline-none focus:border-blue-500 text-xs"
                      value={newProductData.jewelryType}
                      onChange={(e) => setNewProductData({ ...newProductData, jewelryType: e.target.value })}
                    />
                    <datalist id="quick-jewelry-types">
                      <option value="Anel" />
                      <option value="Brinco" />
                      <option value="Colar" />
                      <option value="Pulseira" />
                      <option value="Pingente" />
                      <option value="Aliança" />
                      <option value="Corrente" />
                      <option value="Tornozeleira" />
                    </datalist>
                  </div>

                  <div className="space-y-1">
                    <label className="block text-[9px] text-slate-400 font-black uppercase">Material</label>
                    <input
                      type="text"
                      list="quick-materials"
                      placeholder="Ex: Prata 925"
                      className="w-full p-2.5 border rounded-lg bg-white outline-none focus:border-blue-500 text-xs"
                      value={newProductData.material}
                      onChange={(e) => setNewProductData({ ...newProductData, material: e.target.value })}
                    />
                    <datalist id="quick-materials">
                      <option value="Prata 925" />
                      <option value="Ouro 18k" />
                      <option value="Ouro 10k" />
                      <option value="Folheado a Ouro" />
                      <option value="Folheado a Ródio" />
                      <option value="Aço Inoxidável" />
                    </datalist>
                  </div>

                  <div className="space-y-1">
                    <label className="block text-[9px] text-slate-400 font-black uppercase">Categoria</label>
                    <input
                      type="text"
                      list="quick-categories"
                      placeholder="Ex: Anéis"
                      className="w-full p-2.5 border rounded-lg bg-white outline-none focus:border-blue-500 text-xs"
                      value={newProductData.category}
                      onChange={(e) => setNewProductData({ ...newProductData, category: e.target.value })}
                    />
                    <datalist id="quick-categories">
                      <option value="Anéis" />
                      <option value="Brincos" />
                      <option value="Colares" />
                      <option value="Pulseiras" />
                      <option value="Pingentes" />
                      <option value="Alianças" />
                      <option value="Outros" />
                    </datalist>
                  </div>
                </div>

              </div>

              {/* Actions */}
              <div className="p-4 border-t bg-slate-50 flex justify-end gap-3 shrink-0">
                <button
                  type="button"
                  onClick={() => setIsRegisteringProduct(false)}
                  className="px-4 py-2 border rounded-xl hover:bg-slate-100 text-slate-500 font-bold"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingProduct}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-400 text-white rounded-xl font-extrabold flex items-center gap-1.5 shadow-md shadow-blue-100 active:scale-95 transition-all"
                >
                  {isSubmittingProduct ? (
                    <>
                      <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin mr-1"></div>
                      Cadastrando...
                    </>
                  ) : (
                    <>
                      <Check size={16} /> Salvar Produto
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── HISTÓRICO (NÃO SINCRONIZADOS) SUB-TAB ─── */}
      {activeSubtab === "historico" && (
        <div className="flex-1 overflow-y-auto p-4 md:p-6 custom-scrollbar text-left font-bold text-xs text-slate-700">
          <div className="max-w-6xl mx-auto space-y-4">
            
            <div className="flex justify-between items-center border-b pb-2">
              <div className="flex items-center gap-2">
                <History size={18} className="text-blue-600" />
                <h2 className="text-sm font-extrabold text-slate-800 uppercase">Vendas Pendentes de Sincronização</h2>
              </div>
              <span className="bg-blue-100 text-blue-800 font-bold px-2 py-0.5 rounded">
                Total: {pendingSales.length}
              </span>
            </div>

            {loadingSales ? (
              <div className="flex items-center justify-center py-20 text-slate-400 gap-2">
                <RefreshCw className="animate-spin text-blue-600" /> Carregando histórico...
              </div>
            ) : pendingSales.length === 0 ? (
              <div className="bg-white border rounded-2xl p-12 text-center text-slate-400 shadow-sm">
                <ShoppingCart className="mx-auto mb-2 opacity-15" size={48} />
                <p className="text-xs font-semibold">Nenhuma venda pendente de sincronização.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingSales.map((sale) => (
                  <div key={sale.id} className="bg-white border border-slate-200 rounded-2xl p-4 md:p-5 shadow-sm space-y-4 hover:shadow-md transition-shadow">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-3 font-semibold text-[11px] text-slate-600">
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="font-extrabold text-slate-800 text-sm font-mono">{sale.order_number}</span>
                        <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded flex items-center gap-1 border border-slate-200">
                          <User size={11} /> Vendedor: {sale.seller_name}
                        </span>
                        <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded border border-slate-200">
                          {new Date(sale.created).toLocaleString("pt-BR")}
                        </span>
                        <span className="bg-amber-100 text-amber-800 px-2.5 py-0.5 rounded font-black text-[9px] border border-amber-200 uppercase tracking-wide">
                          Aguardando Sinc.
                        </span>
                      </div>

                      <div className="flex gap-2 flex-wrap">
                        <button
                          onClick={() => handleOpenInvoiceModal(sale)}
                          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-extrabold transition-colors flex items-center gap-1.5 shadow-sm shadow-blue-100"
                          title="Emitir Nota Fiscal Eletrônica"
                        >
                          <FileText size={13} /> Emitir NF-e
                        </button>

                        {!sale.customer_address?.includes("[RETIRADA]") && (
                          <button
                            disabled={labelLoadingSaleId === sale.id}
                            onClick={() => handleGenerateCorreiosLabel(sale)}
                            className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 disabled:bg-slate-400 text-white rounded-lg text-xs font-extrabold transition-colors flex items-center gap-1.5 shadow-sm shadow-amber-100"
                            title="Gerar etiqueta de postagem dos Correios"
                          >
                            {labelLoadingSaleId === sale.id ? (
                              <>
                                <div className="w-3.5 h-3.5 rounded-full border-2 border-white border-t-transparent animate-spin mr-1"></div>
                                Gerando...
                              </>
                            ) : (
                              <>
                                <Truck size={13} /> Etiqueta Correios
                              </>
                            )}
                          </button>
                        )}

                        <button
                          onClick={() => handleSyncSale(sale)}
                          className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-xs font-extrabold transition-colors flex items-center gap-1.5 shadow-sm shadow-emerald-100"
                        >
                          Sincronizar
                        </button>
                        <button
                          onClick={() => handleArchiveSale(sale)}
                          className="px-3 py-1.5 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-extrabold transition-colors flex items-center gap-1.5 shadow-sm"
                        >
                          Arquivar
                        </button>
                        <button
                          onClick={() => handleRevertSale(sale)}
                          className="px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-100 rounded-lg text-xs font-extrabold transition-colors flex items-center gap-1.5"
                        >
                          Reverter Venda
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 bg-slate-50 p-3.5 rounded-xl border border-slate-200/50">
                      <div>
                        <span className="block text-[9px] text-slate-400 uppercase font-black">Cliente</span>
                        <span className="font-bold text-slate-800 text-xs">{sale.customer_name}</span>
                        {(sale.customer_cpf || sale.customer_phone || sale.customer_email) && (
                          <div className="text-[10px] text-slate-500 mt-1 space-y-0.5 font-bold">
                            {sale.customer_cpf && <div>CPF: <span className="font-mono font-medium">{sale.customer_cpf}</span></div>}
                            {sale.customer_phone && <div>Tel: <span className="font-medium">{sale.customer_phone}</span></div>}
                            {sale.customer_email && <div className="truncate">Email: <span className="font-medium">{sale.customer_email}</span></div>}
                          </div>
                        )}
                      </div>
                      <div className="md:col-span-2">
                        <span className="block text-[9px] text-slate-400 uppercase font-black">Endereço de Entrega / Cadastro</span>
                        <span className="font-medium text-slate-600 text-xs line-clamp-2">{sale.customer_address}</span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <span className="block text-[9px] text-slate-400 uppercase font-black tracking-widest pl-1 mb-1">Itens Vendidos</span>
                      <div className="divide-y divide-slate-100 border border-slate-200/80 rounded-xl overflow-hidden bg-slate-50/20">
                        {sale.items?.map((item, index) => (
                          <div key={index} className="p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center text-slate-500 border text-[10px] font-bold shrink-0">
                                {item.sku.slice(0, 3)}
                              </div>
                              <div className="text-left">
                                <div className="flex items-center gap-2">
                                  <span className="font-bold text-slate-800 text-xs">{item.sku}</span>
                                  <span className={`text-[8px] px-1.5 py-0.5 rounded font-black border ${
                                    item.source === "estoque"
                                      ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                                      : "bg-purple-50 text-purple-700 border-purple-100"
                                  }`}>
                                    {item.source === "estoque" ? "Estoque" : "Produção"}
                                  </span>
                                </div>
                                <p className="text-[10px] text-slate-500 font-medium">{item.name}</p>
                              </div>
                            </div>

                            {item.specs && (
                              <div className="flex flex-wrap gap-1.5 text-[9px] font-bold text-slate-500">
                                {item.specs.size && <span className="bg-slate-100 border px-1.5 py-0.5 rounded">Aro: {item.specs.size}</span>}
                                {item.specs.stoneColor && <span className="bg-slate-100 border px-1.5 py-0.5 rounded">Pedra: {item.specs.stoneColor}</span>}
                                {item.specs.finishing && <span className="bg-slate-100 border px-1.5 py-0.5 rounded">Banho: {item.specs.finishing}</span>}
                                {item.specs.engraving && <EngravingDisplay rawText={item.specs.engraving} mode="compact" />}
                                {item.specs.exception && <span className="bg-red-50 text-red-700 border border-red-100 px-1.5 py-0.5 rounded">Aviso: {item.specs.exception}</span>}
                              </div>
                            )}

                            <div className="text-right shrink-0 font-bold text-xs text-slate-800 min-w-[120px]">
                              <span>{item.quantity} un × {formatMoney(item.price)}</span>
                              <p className="text-[10px] text-slate-400 font-medium">Subtotal: {formatMoney(item.price * item.quantity)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="bg-slate-900 text-white px-4 py-3 rounded-xl flex flex-wrap justify-between items-center gap-3 shadow-md">
                      <div className="flex gap-4 text-[10px] opacity-80">
                        <span>Método: <b className="text-white uppercase">{sale.payment_method}</b></span>
                        <span>Subtotal: <b className="text-white">{formatMoney(sale.subtotal)}</b></span>
                        {sale.discount > 0 && <span>Desconto: <b className="text-rose-400">- {formatMoney(sale.discount)}</b></span>}
                      </div>
                      <div className="text-right">
                        <span className="text-[10px] font-medium opacity-70 mr-1.5">Valor Pago:</span>
                        <span className="text-base font-extrabold text-emerald-400">{formatMoney(sale.total)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── SINCRONIZADOS TAB ─── */}
      {activeSubtab === "sincronizados" && (
        <div className="flex-1 overflow-y-auto p-4 md:p-6 custom-scrollbar text-left font-bold text-xs text-slate-700">
          <div className="max-w-6xl mx-auto space-y-4">
            
            <div className="flex justify-between items-center border-b pb-2">
              <div className="flex items-center gap-2">
                <CheckCircle size={18} className="text-emerald-600" />
                <h2 className="text-sm font-extrabold text-slate-800 uppercase">Vendas Sincronizadas</h2>
              </div>
              <span className="bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded">
                Total: {syncedSales.length}
              </span>
            </div>

            {loadingSales ? (
              <div className="flex items-center justify-center py-20 text-slate-400 gap-2">
                <RefreshCw className="animate-spin text-blue-600" /> Carregando...
              </div>
            ) : syncedSales.length === 0 ? (
              <div className="bg-white border rounded-2xl p-12 text-center text-slate-400 shadow-sm">
                <Archive className="mx-auto mb-2 opacity-15" size={48} />
                <p className="text-xs font-semibold">Nenhuma venda sincronizada encontrada.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {syncedSales.map((sale) => (
                  <div key={sale.id} className="bg-white border border-slate-200/80 rounded-2xl p-4 md:p-5 shadow-sm space-y-3">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-3 text-[11px] text-slate-500">
                      <div className="flex items-center gap-3">
                        <span className="font-extrabold text-slate-800 text-sm font-mono">{sale.order_number}</span>
                        <span>Vendedor: {sale.seller_name}</span>
                        <span>{new Date(sale.created).toLocaleString("pt-BR")}</span>
                        <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded font-black text-[9px] border border-emerald-200 uppercase tracking-wide flex items-center gap-1">
                          <CheckCircle size={10} /> Sincronizado
                        </span>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => handleOpenInvoiceModal(sale)}
                          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-extrabold transition-colors flex items-center gap-1.5 shadow-sm shadow-blue-100"
                          title="Emitir Nota Fiscal Eletrônica"
                        >
                          <FileText size={13} /> Emitir NF-e
                        </button>

                        {!sale.customer_address?.includes("[RETIRADA]") && (
                          <button
                            disabled={labelLoadingSaleId === sale.id}
                            onClick={() => handleGenerateCorreiosLabel(sale)}
                            className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 disabled:bg-slate-400 text-white rounded-lg text-xs font-extrabold transition-colors flex items-center gap-1.5 shadow-sm shadow-amber-100"
                            title="Gerar etiqueta de postagem dos Correios"
                          >
                            {labelLoadingSaleId === sale.id ? (
                              <>
                                <div className="w-3.5 h-3.5 rounded-full border-2 border-white border-t-transparent animate-spin mr-1"></div>
                                Gerando...
                              </>
                            ) : (
                              <>
                                <Truck size={13} /> Etiqueta Correios
                              </>
                            )}
                          </button>
                        )}

                        <button
                          onClick={() => handleArchiveSale(sale)}
                          className="px-3 py-1.5 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5"
                        >
                          Arquivar
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 bg-slate-50/50 p-3.5 rounded-xl border">
                      <div>
                        <span className="block text-[9px] text-slate-400 uppercase font-black">Cliente</span>
                        <span className="font-bold text-slate-700 text-xs">{sale.customer_name}</span>
                        {(sale.customer_cpf || sale.customer_phone || sale.customer_email) && (
                          <div className="text-[10px] text-slate-500 mt-1 space-y-0.5 font-bold">
                            {sale.customer_cpf && <div>CPF: <span className="font-mono font-medium">{sale.customer_cpf}</span></div>}
                            {sale.customer_phone && <div>Tel: <span className="font-medium">{sale.customer_phone}</span></div>}
                            {sale.customer_email && <div className="truncate">Email: <span className="font-medium">{sale.customer_email}</span></div>}
                          </div>
                        )}
                      </div>
                      <div className="md:col-span-2">
                        <span className="block text-[9px] text-slate-400 uppercase font-black">Endereço</span>
                        <span className="font-medium text-slate-600 text-xs line-clamp-1">{sale.customer_address}</span>
                      </div>
                    </div>

                    <div className="bg-slate-100 p-3.5 rounded-xl flex justify-between items-center">
                      <div className="flex gap-4 text-[10px] text-slate-500 font-bold">
                        <span>Método: <b className="text-slate-700 uppercase">{sale.payment_method}</b></span>
                        <span>Itens: <b className="text-slate-700">{sale.items?.reduce((a, b) => a + b.quantity, 0)}</b></span>
                      </div>
                      <div className="text-right">
                        <span className="text-sm font-black text-slate-800">Total Pago: {formatMoney(sale.total)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── DASHBOARD SUB-TAB ─── */}
      {activeSubtab === "dashboard" && (
        <div className="flex-1 overflow-y-auto p-4 md:p-6 custom-scrollbar text-left font-bold text-xs text-slate-700">
          <div className="max-w-6xl mx-auto space-y-6">
            
            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
              <div>
                <h2 className="text-sm font-extrabold text-slate-800 uppercase flex items-center gap-1.5">
                  <TrendingUp className="text-blue-600" /> Dashboard PDV Showroom
                </h2>
                <p className="text-[10px] text-slate-400 mt-0.5">Visão de performance e faturamento presencial showroom.</p>
              </div>

              <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
                <div className="flex bg-slate-100 p-0.5 rounded-xl border text-[10px]">
                  {["7", "15", "30", "all"].map((p) => (
                    <button
                      key={p}
                      onClick={() => setDashPeriodRange(p === "all" ? "365" : p)}
                      className={`px-3 py-1.5 rounded-lg font-bold transition-all ${
                        dashPeriod === p || (dashPeriod === "365" && p === "all")
                          ? "bg-white text-blue-700 shadow-sm"
                          : "text-slate-500 hover:text-slate-800"
                      }`}
                    >
                      {p === "all" ? "Tudo" : `${p}D`}
                    </button>
                  ))}
                  <button
                    onClick={() => setDashPeriod("custom")}
                    className={`px-3 py-1.5 rounded-lg font-bold transition-all ${
                      dashPeriod === "custom" ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    Personalizado
                  </button>
                </div>

                {dashPeriod === "custom" && (
                  <div className="flex items-center gap-2 shrink-0 bg-slate-50 p-1 rounded-xl border border-slate-200 animate-fade-in">
                    <input
                      type="date"
                      className="bg-transparent border-0 font-bold text-slate-700 outline-none text-[10px] p-1"
                      value={dashStartDate}
                      onChange={(e) => setDashStartDate(e.target.value)}
                    />
                    <span className="text-slate-400">até</span>
                    <input
                      type="date"
                      className="bg-transparent border-0 font-bold text-slate-700 outline-none text-[10px] p-1"
                      value={dashEndDate}
                      onChange={(e) => setDashEndDate(e.target.value)}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm text-left flex flex-col justify-between h-28">
                <span className="text-[9px] text-slate-400 uppercase font-black tracking-wider">Faturamento Showroom</span>
                <p className="text-xl font-black text-slate-800 tracking-tight mt-1">{formatMoney(dashboardData.faturamento)}</p>
                <div className="mt-auto flex items-center gap-1.5 text-[9px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded w-fit">
                  <TrendingUp size={10} /> Em Vendas
                </div>
              </div>

              <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm text-left flex flex-col justify-between h-28">
                <span className="text-[9px] text-slate-400 uppercase font-black tracking-wider">Total de Pedidos</span>
                <p className="text-2xl font-black text-slate-800 tracking-tight mt-1">{dashboardData.vendasCount}</p>
                <div className="mt-auto text-[9px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded w-fit uppercase">
                  Pedidos Fechados
                </div>
              </div>

              <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm text-left flex flex-col justify-between h-28">
                <span className="text-[9px] text-slate-400 uppercase font-black tracking-wider">Ticket Médio</span>
                <p className="text-xl font-black text-slate-800 tracking-tight mt-1">{formatMoney(dashboardData.ticketMedio)}</p>
                <div className="mt-auto text-[9px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded w-fit uppercase">
                  Média por Venda
                </div>
              </div>

              <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm text-left flex flex-col justify-between h-28">
                <span className="text-[9px] text-slate-400 uppercase font-black tracking-wider">Peças Vendidas</span>
                <p className="text-2xl font-black text-slate-800 tracking-tight mt-1">{dashboardData.totalItens}</p>
                <div className="mt-auto text-[9px] font-bold text-purple-600 bg-purple-50 px-2 py-0.5 rounded w-fit uppercase">
                  Unidades Faturadas
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex flex-col h-80 lg:col-span-2">
                <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest text-left mb-4 flex items-center gap-1.5">
                  <Calendar size={13} /> Evolução de Vendas Diárias (R$)
                </span>
                <div className="flex-1 w-full text-xs font-bold text-slate-500">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={dashboardData.trendData}>
                      <defs>
                        <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#2563eb" stopOpacity={0.2} />
                          <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="date" stroke="#94a3b8" fontSize={9} />
                      <YAxis stroke="#94a3b8" fontSize={9} tickFormatter={(val) => `R$ ${val}`} />
                      <Tooltip formatter={(value) => [formatMoney(value), "Faturamento"]} />
                      <Area type="monotone" dataKey="Total" stroke="#2563eb" strokeWidth={2} fillOpacity={1} fill="url(#colorTotal)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex flex-col h-80 text-left">
                <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-1.5">
                  <CreditCard size={13} /> Métodos de Pagamento
                </span>
                <div className="flex-1 flex flex-col divide-y divide-slate-100 overflow-y-auto custom-scrollbar">
                  {dashboardData.pagamentosList.length === 0 ? (
                    <div className="flex-1 flex items-center justify-center text-slate-400 text-xs">
                      Sem dados no período.
                    </div>
                  ) : (
                    dashboardData.pagamentosList.map((item, idx) => (
                      <div key={idx} className="py-3 flex items-center justify-between font-bold text-slate-700 text-xs">
                        <span>{item.name}</span>
                        <div className="text-right">
                          <span className="text-slate-800">{formatMoney(item.value)}</span>
                          <p className="text-[9px] text-slate-400 font-medium">
                            {((item.value / (dashboardData.faturamento || 1)) * 100).toFixed(1)}% do total
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex flex-col text-left">
              <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-1.5">
                <Compass size={13} /> Ranking de SKUs Mais Vendidos (Showroom)
              </span>
              <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden">
                {dashboardData.skusList.length === 0 ? (
                  <div className="p-8 text-center text-slate-400 text-xs font-semibold">
                    Nenhum item faturado no período selecionado.
                  </div>
                ) : (
                  dashboardData.skusList.map((item, idx) => {
                    const catalogItemInfo = findCatalogItem(item.sku);
                    return (
                      <div key={idx} className="p-3.5 flex items-center justify-between bg-white hover:bg-slate-50/50">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded bg-blue-100 text-blue-800 flex items-center justify-center text-xs font-black">
                            #{idx + 1}
                          </div>
                          <div>
                            <span className="font-extrabold text-slate-800 text-xs">{item.sku}</span>
                            <p className="text-[10px] text-slate-400 font-medium">{catalogItemInfo?.name || "Sem Nome"}</p>
                          </div>
                        </div>
                        <div className="bg-blue-50 border border-blue-100 px-3 py-1 rounded-lg text-blue-700 font-black text-xs">
                          {item.qty} {item.qty === 1 ? "Unidade" : "Unidades"}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE EMISSÃO DE NOTA FISCAL (NF-e) */}
      {isInvoiceModalOpen && selectedSaleForInvoice && (
        <InvoiceGenerationModal
          isOpen={isInvoiceModalOpen}
          onClose={() => {
            setIsInvoiceModalOpen(false);
            setSelectedSaleForInvoice(null);
          }}
          orderGroup={selectedSaleForInvoice}
          findCatalogItem={findCatalogItem}
          onSuccess={() => {
            showNotification && showNotification("Solicitação de Nota Fiscal enviada!", "success");
          }}
        />
      )}
    </div>
  );
}
